import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {SpinnerButton} from '../../SpinnerButton';
import {FaIcon} from '../../FaIcon';
import BudgetValueInput from '../../elements/BudgetValueInput';
import {CurrencySelect} from '../../elements/CurrencySelect';
// Componentes CANÓNICOS del editor de líneas de control (feedback Pablo/fede 2026-07-30): la
// propuesta usa LOS MISMOS componentes con los que el usuario ya configura una línea — switch de
// objetivo + combobox de métrica + valor, y el configurador real de tramos. Nada de réplicas.
import {Switch} from '../../buttons/Swtich';
import {DecimalInput} from '../../elements/DecimalInput';
import {MetricCombobox} from '../../dashboard/MetricCombobox';
import BudgetPartialGoalsConfiguration from '../../elements/BudgetPartialGoalsConfiguration';
import {BudgetHelper} from '../../../utils/BudgetHelper';
import {fetchCustomMetrics, filterForGoals} from '../../../hooks/useCustomMetrics';
import {MediaPlanApi} from '../../../api/mediaPlanApi';
import {getChannelVisual} from '../../agency/CustomMetricEditor/metric_search';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {CellPeek} from './CellPeek';
import {cellUrl, spreadsheetUrl} from './sheetUrl';
import {MediaPlanEmptyState} from './MediaPlanEmptyState';
import {useScanFreshness} from './useScanFreshness';
import {proposalSummary} from './proposalSummary';
import {rowIdentity} from './rowIdentity';
import {resolveExpandedIdentity} from './accordionFocus';
import {planCurrencyState} from './planCurrency';
import {partitionProposalQuestions, untrackedOptionLabelKey} from './proposalQuestions';
import {basisChangeOptions, basisSourceKey, columnLabelOf, planBasisState, tableNumber} from './planBasis';
import {NEW_LINE_NAME_MAX_LENGTH, TARGET_NEW, TARGET_SECTION, amountSource, canLeaveUndecided, canSaveMapping,
    choiceFromValue, choiceValue, dismissPayload, effectiveChoices, groupMappingState, implicitLineNames,
    isImplicitUntracked, isSplitGroup, lineTotals, mappingPayload, newNameTaken, offersSection, suggestionReasonKey,
    untrackedPlatforms} from './groupMapping';
import {isNetworkError} from '../../../utils/apiResponse';
import {amountWithoutOfferFor, dv360OfferOf, untrackedRowsOf} from './untrackedRows';
import {readingUpgradeVisible} from './readingUpgrade';
import {InterpretationReview} from './InterpretationReview';

// Pantalla de revisión de la propuesta del media plan (F1 — SSOT §8b). Rediseño 2026-07-10 (jerarquía
// "resultado primero"): resumen + cambios propuestos arriba (el valor), las dudas de identificación de
// campañas —opcionales, refinan el detalle— colapsan en un acordeón, y los bloqueos (plataforma no
// soportada, cuenta sin sincronizar) suben como avisos. Los objetivos con competencia objetivo-vs-real
// (channel_objectives) se resuelven con un radio "elegí A o B" con el objetivo sugerido pre-marcado.
const NS = 'media_plan.proposal';
const APPLIABLE = new Set(['CREATE_BU', 'UPDATE_BU_BUDGET', 'UPDATE_BU_CAMPAIGNS',
    'UPDATE_CAMPAIGN_BUDGETS', 'UPDATE_GOALS']);
// Key de la opción "Asignar más adelante" de las dudas de alias (contrato con el backend,
// 2026-07-22): se renderiza como acción secundaria, nunca entre las candidatas.
const DEFER_KEY = 'defer';
const PLAN_DECISION_COPY = {
    period: {title: 'period_title', hint: 'period_hint'},
    account: {title: 'decision.account_title', hint: 'decision.account_hint'},
    budget: {title: 'decision.budget_title', hint: 'decision.budget_hint'},
    generic: {title: 'decision.generic_title', hint: 'decision.generic_hint'},
    currency: {title: 'plan_currency.label', hint: 'decision.currency_hint'},
};

// Gate de resolución completa (2026-07-22): la línea de CAMPAÑAS del plan (flag `campaigns_line`
// del payload, presente también en UPDATE_BU_CAMPAIGNS) no se aprueba mientras queden campañas sin
// resolver — ni identificadas ni pospuestas ("asignar más adelante"). Espeja `pending_unmatched`
// del backend (el endpoint rechaza el approve igual; esto evita el 400 con el botón deshabilitado).
function pendingUnmatched(payload) {
    return ((payload || {}).unmatched_campaigns || []).filter((u) => !u.deferred);
}
function deferredUnmatched(payload) {
    return ((payload || {}).unmatched_campaigns || []).filter((u) => u.deferred);
}
function isGatedItem(item) {
    return !!(item.payload || {}).campaigns_line;
}
// Prioridad de "pide acción" de una fila (acordeón UX 2.1): >0 = accionable. Ordena el auto-open de la
// primera fila abierta al cargar: conflicto (3) > falta identificar (2) > a revisar (1) > nada (0, ya
// resuelta/aplicada/rechazada). Espeja la semántica de `rowStatus` sin depender de `t`.
function rowActionPriority(item) {
    if (item.review_status !== 'PENDING_REVIEW') return 0;
    // Solo el conflicto RESOLUBLE (GOAL_MISMATCH, el del radio A/B) pide acción: los demás kinds
    // muestran un hint estático y no deben robarle el auto-open a una fila que sí hay que trabajar.
    if (item.action_type === 'CONFLICT') {
        return (item.payload || {}).kind === 'GOAL_MISMATCH' ? 3 : 0;
    }
    if (isGatedItem(item) && pendingUnmatched(item.payload).length > 0) return 2;
    if (APPLIABLE.has(item.action_type)) return 1;
    return 0;
}
// ¿La fila se puede APROBAR directo desde su header (sin abrirla)? Solo si es una acción aplicable
// PENDIENTE y no le queda nada por resolver primero: ni campañas por identificar (gate), ni decisión
// de destino (link_candidates). Las que sí tienen trabajo previo muestran el chip ("Falta identificar",
// "Con conflicto") que invita a abrir y resolver adentro.
// Largo máximo del nombre de una línea de control: el mismo `BusinessUnit.NAME_MAX_LENGTH` del
// backend. Se corta acá para que el límite se sienta al escribir y no como un error al aplicar.
const NAME_MAX_LENGTH = 100;

// Contenedor que realmente scrollea alrededor de un nodo (o `null` = el documento). El marco de la
// UX 2.1 puede meter su propio scroll container, así que restaurar la posición con `window.scrollBy`
// a ciegas movería la ventana equivocada.
function scrollableAncestor(node) {
    let el = node && node.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
        const overflowY = (window.getComputedStyle(el) || {}).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

function rowReadyToApprove(item) {
    if (item.review_status !== 'PENDING_REVIEW') return false;
    if (!APPLIABLE.has(item.action_type)) return false;
    const payload = item.payload || {};
    if (isGatedItem(item) && pendingUnmatched(payload).length > 0) return false;
    if (item.action_type === 'CREATE_BU' && (payload.link_candidates || []).length > 0) return false;
    // Objetivo con competencia objetivo-vs-real (radio A/B): aprobar a ciegas aplicaría el candidato
    // sugerido sin que el humano lo haya visto. Se resuelve adentro, con la fila abierta (D11: el
    // sistema sugiere, el humano confirma).
    if ((payload.goals || []).some((g) => (g.candidates || []).length > 0)) return false;
    return true;
}
// Warnings del snapshot que SÍ son avisos accionables para el HITL (ALLOWLIST estricta). El snapshot
// mezcla tres fuentes: ruido de celdas con error del grid (#REF!/#DIV/0!), warnings del LLM con
// `type` libre (no confiables), y estos avisos de cobertura bien tipados del parse. Solo mostramos
// los últimos. CHANNEL_OBJECTIVES_AMBIGUOUS queda afuera: ya se resuelve inline con el radio A/B.
const SNAPSHOT_NOTE_TYPES = new Set(['FLIGHT_CHECKPOINTS_INCOMPLETE', 'CHANNEL_OBJECTIVES_INCOMPLETE',
    'BUDGET_GRID_UNAVAILABLE']);
// Avisos del plan efectivo (`proposal.warnings`, tanda «grupos, dinero y DV360»): el código leyó el
// monto de otra celda que la que citó la IA, o una fila con monto no tiene campaña. Informativos: un
// aviso azul por tipo, con todas sus celdas. Allowlist igual que la de arriba. `PLATFORM_NOT_TRACKED` (ronda 2):
// una plataforma sin canal dentro de un split, con su plata; se dice con `platform` y `amount`, no con sus celdas.
const PROPOSAL_WARNING_TYPES = ['ROW_NOT_READ', 'REANCHORED', 'PLATFORM_NOT_TRACKED'];
const CONFIDENCE_CLASS = {HIGH: '-good', MEDIUM: '-warn', LOW: '-bad'};
const PLATFORM_LABEL = {GOOGLE: 'Google', FACEBOOK: 'Facebook', META: 'Meta', TIKTOK: 'TikTok', LINKEDIN: 'LinkedIn'};
// Media plan → canal para <ChannelLogo> (los logos de canal A COLOR, los mismos de Monitoreo/Dashboard/
// límites diarios). OJO: el codebase representa Meta como 'FACEBOOK' (channels.js) → mapear 'META'.
const PLATFORM_TO_CHANNEL = {GOOGLE: 'GOOGLE', META: 'FACEBOOK', FACEBOOK: 'FACEBOOK',
    TIKTOK: 'TIKTOK', LINKEDIN: 'LINKEDIN'};
// Descriptor de la CUENTA publicitaria por canal ("Cuenta de Google Ads"): el header del grupo de
// dudas no decía qué era ese nombre (feedback fede 2026-07-18: "¿eso es una cuenta?").
const PLATFORM_ADS_LABEL = {GOOGLE: 'Google Ads', META: 'Meta Ads', FACEBOOK: 'Meta Ads',
    TIKTOK: 'TikTok Ads', LINKEDIN: 'LinkedIn Ads'};

// Visual del canal (logo + color de marca) desde el SSOT CHANNEL_VISUAL de la plataforma. El media
// plan etiqueta el canal de Meta como META o FACEBOOK según el origen del payload → ambos mapean
// al visual META. null si el canal no tiene visual (el header degrada a solo texto).
function platformVisual(platform) {
    if (!platform) return null;
    return getChannelVisual(platform === 'FACEBOOK' ? 'META' : platform);
}

// La plataforma de una card, para agrupar las dos líneas de un mismo canal (global + campañas) bajo
// un encabezado (2026-07-24) — deriva de su 1ª cuenta. Null = card sin plataforma única (BU3
// cross-plataforma, updates sin cuentas) → sin encabezado, se renderiza en secuencia (degrada bien).
function cardPlatform(item) {
    const p = item.payload || {};
    if (p.advisory) return null;                 // BU3 (objetivo global cross-plataforma)
    const account = (p.accounts || [])[0];
    return account ? account.platform : null;
}

// Clave de agrupamiento de una duda de alias por cuenta (para agrupar en el acordeón y ligarla a su
// card). Las dudas sin grupo (ej. la de cuenta) caen a una clave vacía → grupo sin encabezado.
function questionGroupKey(platform, accountName) {
    return `${platform || ''}::${accountName || ''}`;
}

// Ancla compacta de una duda: la campaña del plan. Prefiere el `subject` del backend; si falta (una
// propuesta generada antes de ese campo), la extrae del propio texto (la 1ª «...»). Así la fila
// compacta funciona sin re-leer la planilla.
function questionAnchor(question) {
    if (question.subject) return question.subject;
    const match = /«([^»]+)»/.exec(question.question_text || '');
    return match ? match[1] : null;
}

// Agrupa las dudas de alias por cuenta (`question.group` = {platform, account_name}), preservando el
// orden de aparición. El backend etiqueta cada duda con su cuenta para que se lean junto a su card.
function groupQuestions(questions) {
    const groups = [];
    const byKey = {};
    for (const q of questions) {
        const g = (q.question && q.question.group) || {};
        const key = questionGroupKey(g.platform, g.account_name);
        if (!byKey[key]) {
            byKey[key] = {key, platform: g.platform || '', accountName: g.account_name || '', questions: []};
            groups.push(byKey[key]);
        }
        byKey[key].questions.push(q);
    }
    return groups;
}

// La opción SUGERIDA de una duda de alias (match con confianza), o null si no hay una (la mejor
// candidata del ranking NO es una recomendación → no se auto-confirma a ciegas). Es el criterio del
// "confirmar N sugeridas" y espeja el `hasSuggestion` del render: solo `suggested`/`recommended`.
function suggestedAliasOption(question) {
    if (!question || !question.alias_pin_key) return null;   // solo dudas de alias, no de cuenta
    return (question.options || []).find((o) => o.suggested || o.recommended) || null;
}

// Los valores objetivo salen de fórmulas de la planilla → llegan con precisión de float
// (10.501427…). Se redondean para APLICAR (el target real: ROAS 10,5 / CPA 19.625) y se formatean
// es-AR para MOSTRAR. Regla por magnitud: valores grandes (CPA/CPM) enteros, chicos (ROAS/CTR) 2 dec.
function roundGoalValue(value) {
    const n = Number(value);
    if (!isFinite(n)) return value;
    return Math.abs(n) >= 1000 ? Math.round(n) : Math.round(n * 100) / 100;
}
function formatGoalValue(value) {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (!isFinite(n)) return String(value);
    const digits = Math.abs(n) >= 1000 ? 0 : 2;
    return new Intl.NumberFormat('es-AR', {maximumFractionDigits: digits}).format(n);
}

// Presupuesto de una línea candidata → 'ARS 40.000.000' (separadores de miles es-AR). La moneda va
// como prefijo suelto (el código ISO que manda el backend); si falta el monto, degrada a solo moneda.
function formatBudgetWithCurrency(amount, currency) {
    const cur = currency ? `${currency} ` : '';
    if (amount === null || amount === undefined || amount === '') return cur.trim();
    const n = Number(amount);
    if (!isFinite(n)) return `${cur}${amount}`;
    return `${cur}${new Intl.NumberFormat('es-AR', {maximumFractionDigits: 0}).format(n)}`;
}

// Un monto del plan que puede faltar (una fila con la celda vacía, un grupo sin montos): «—» y no la
// moneda suelta, que se leía como un número que no cargó.
function formatPlanAmount(amount, currency) {
    if (amount === null || amount === undefined || amount === '' || !isFinite(Number(amount))) return '—';
    return formatBudgetWithCurrency(amount, currency);
}

// Celdas de un aviso: hasta 5 y «y N más». Un aviso por tabla puede traer decenas.
const WARNING_CELLS_SHOWN = 5;

// La candidata SUGERIDA de una card de vinculación (a lo sumo una, por contrato), o null. Espeja el
// criterio del bulk "Vincular las N sugeridas": solo entran las cards que traen una sugerida clara.
function suggestedLinkCandidate(item) {
    const candidates = (item.payload || {}).link_candidates || [];
    return candidates.find((c) => c.suggested) || null;
}

// El `answer_key` PRE-MARCADO de una card de vinculación cuando el usuario no tocó ningún radio: la
// candidata sugerida (`link::<bu_id>`) si la hay, sino "crear nueva". ÚNICA fuente del default —
// consumida por el render (radio marcado) Y por confirmLink (para que "Confirmar destino" sin tocar
// el radio mande igual el destino pre-marcado; un radio ya-checked no dispara onChange).
function defaultLinkAnswerKey(item) {
    const suggested = suggestedLinkCandidate(item);
    return suggested ? `link::${suggested.bu_id}` : 'create_new';
}

// Día del mes de un tramo (`BudgetPartialGoal`, '20260719' → 19). El configurador de tramos de la
// línea razona por DÍA
// del período ("Al día 19 quiero haber invertido…"), no por fecha completa. Si el formato no es el
// esperado, degrada a la fecha cruda (nunca deja el renglón vacío).
function checkpointDay(yyyymmdd) {
    const s = String(yyyymmdd || '');
    return /^\d{8}$/.test(s) ? parseInt(s.slice(6, 8), 10) : s;
}

// Bordes del mes EN CURSO como Dates — las líneas del plan son mensuales desde el día 1 (contrato
// del backend), y el configurador real de tramos los necesita para armar el select de días.
function monthBounds() {
    const now = new Date();
    return {
        monthFirstDay: new Date(now.getFullYear(), now.getMonth(), 1),
        monthLastDay: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    };
}

// Tramos del payload ('YYYYMMDD' del backend) → filas del configurador real (Date + value + type).
// La vuelta (patch del approve) la hace `BudgetHelper.BudgetPartialGoalTranslateToSubmit`.
function partialGoalRowsOf(payload) {
    return (payload.budget_partial_goals || []).map((pg) => ({
        date: new Date(parseInt(String(pg.date).slice(0, 4), 10),
            parseInt(String(pg.date).slice(4, 6), 10) - 1,
            parseInt(String(pg.date).slice(6, 8), 10)),
        value: pg.value,
        type: pg.type || 'PERCENTAGE_GOAL',
    }));
}

export function MediaPlanProposalView({clientId, connection, firstReadRunning, onSnapshotInfo,
                                       tabScan, onPickTab, onSummaryChanged}) {
    const {t} = useTranslation();
    const [proposal, setProposal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [interpretationOpen, setInterpretationOpen] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [applying, setApplying] = useState(false);
    const [busyItemId, setBusyItemId] = useState(null);
    const [confirmingSuggested, setConfirmingSuggested] = useState(false);
    const [deferringRemaining, setDeferringRemaining] = useState(false);  // bulk "Posponer las N" en curso
    const [editedBudgets, setEditedBudgets] = useState({});
    const [questionBudgets, setQuestionBudgets] = useState({}); // respuestas numéricas budget_for::
    const [editedGoals, setEditedGoals] = useState({});   // {itemId: {goalIndex: value}}
    const [questionsOpen, setQuestionsOpen] = useState(false);
    const [expandedQuestions, setExpandedQuestions] = useState({});   // {qid: bool} — ver más opciones
    const [expandedDeferred, setExpandedDeferred] = useState({});     // {qid: bool} — "Elegir campaña" de una pospuesta desplegado
    const [deferredOpen, setDeferredOpen] = useState({});             // {groupKey: bool} — sección "Pospuestas" desplegada
    const [collapsedCardIds, setCollapsedCardIds] = useState({});     // {itemId: true} — identificación inline plegada (default: abierta)
    const [linkChoices, setLinkChoices] = useState({});               // {itemId: answer_key} — radio de destino sin confirmar
    const [conflictChoices, setConflictChoices] = useState({});       // {itemId: 'use_plan'|'keep_mine'} — radio A/B del conflicto de objetivo (default keep_mine)
    const [linkOptionsOpen, setLinkOptionsOpen] = useState({});       // {itemId: bool} — "vincular con otra línea…" desplegado
    const [linkingSuggested, setLinkingSuggested] = useState(false);  // bulk "Vincular las N sugeridas" en curso
    const [undoTarget, setUndoTarget] = useState(null);               // {lineKey, where} — confirmación inline de "deshacer destino" abierta (una a la vez)
    const [undoingLink, setUndoingLink] = useState(false);            // undo del destino en curso
    const [savingCurrency, setSavingCurrency] = useState(false);      // cambio de la moneda del plan en curso (regenera)
    const [currencyPickerOpen, setCurrencyPickerOpen] = useState(false); // «Cambiar» la moneda del plan desplegado
    // Tanda «grupos, dinero y DV360»: base de los montos y reparto grupo → línea.
    const [savingBasis, setSavingBasis] = useState(null);            // firma de la tabla cuya base se está guardando (regenera)
    const [basisPickerFor, setBasisPickerFor] = useState(null);      // firma de la tabla con «Cambiar» desplegado
    // Lo que el usuario tocó en el reparto, por CLAVE de grupo (estable entre regeneraciones, a
    // diferencia de los ids de los items). `null` = volvió a «Elegir…». Lo no tocado sale de lo
    // guardado o lo sugerido (`effectiveChoices`). Se limpia al guardar.
    const [mappingEdits, setMappingEdits] = useState({});
    const [savingMapping, setSavingMapping] = useState(null);
    // Qué escaneo explica la pantalla: el de la última relectura gana hasta que llega uno nuevo por
    // la conexión. La regla vive en su propio módulo para poder testearla de verdad.
    const {scan: effectiveScan, repeatedFailure, recordScan} = useScanFreshness(tabScan);
    // Componentes canónicos del editor de BU (2026-07-30):
    const [customMetrics, setCustomMetrics] = useState([]);           // métricas custom para el MetricCombobox (mismas del editor)
    const [editedGoalMetrics, setEditedGoalMetrics] = useState({});   // {itemId: {i: metricName}} — métrica cambiada en el combobox
    const [disabledGoals, setDisabledGoals] = useState({});           // {itemId: {i: true}} — objetivo apagado con el switch (no va al patch)
    const [addedGoals, setAddedGoals] = useState({});                 // {itemId: {enabled, metricName, value}} — fila "Objetivo #2" agregada a mano
    const [goalPicks, setGoalPicks] = useState({});                   // {itemId: {i: n}} — contador de picks del radio A/B (remonta el DecimalInput)
    const [editedPartialGoals, setEditedPartialGoals] = useState({}); // {itemId: {budgetPartialGoals: rows}} — tramos editados en el configurador real
    const [goalCampaignsOpen, setGoalCampaignsOpen] = useState({});    // {itemId: bool} — "Objetivos por campaña" de UPDATE_GOALS desplegado
    const groupRefs = useRef({});          // clave de grupo → nodo del acordeón (para scrollear)
    // Nombre editado, keyed por IDENTIDAD de la línea y no por PK: una regeneración le cambia el
    // `item.id` a todas las filas, y con el id el nombre que el usuario acababa de escribir se perdía
    // en silencio al responder una duda. En el piloto ese es el recorrido normal —nombrar la línea y
    // después identificar sus campañas—, así que el nombre se perdía casi siempre.
    const [editedNames, setEditedNames] = useState({});               // {rowIdentity: string}
    // UX 2.1 (rediseño en acordeón): fila expandida (exclusivo — una sola abierta). `undefined` = sin
    // inicializar → un efecto abre la 1ra fila que pide acción; `null` = todas colapsadas.
    //
    // La fuente de verdad es la IDENTIDAD ESTABLE de la fila (`line::<line_key>`), NO su PK. El
    // backend crea propuesta e items con PKs nuevas en cada regeneración (responder una duda,
    // vincular, posponer), así que con el id como fuente el render siguiente no encontraba ninguna
    // fila abierta: el acordeón se cerraba ENTERO por un frame, el documento se acortaba cientos de
    // píxeles, el navegador clampeaba el scroll al nuevo alto y —cuando el efecto volvía a abrir la
    // fila— el usuario ya estaba arriba de todo. Eso es el "se cerró y se fue para arriba" con 19
    // campañas por identificar (card jiJyztGD). Con la identidad, la MISMA fila se dibuja abierta en
    // el MISMO render: no hay frame colapsado que un efecto tenga que corregir después.
    const [expandedIdentity, setExpandedIdentity] = useState(undefined);
    const autoOpenedProposalId = useRef(null);   // última propuesta cuya fila accionable ya se auto-abrió
    const expandedShapeRef = useRef(null);       // {index, actionType} de la fila abierta, para reencontrarla si CAMBIA de forma
    const rowNodes = useRef({});                 // identidad → nodo <li> de la fila (medir/restaurar el scroll)
    const pendingAnchor = useRef(null);          // {identity, top} tomado antes de una acción que regenera
    // El acordeón es la vista por defecto de la propuesta, para todos. No hay flag detrás: queda
    // como constante (y no inline) para poder volver a la vista lista poniendo `false` — `renderItem`
    // sigue vivo y es la otra rama de este mismo if.
    const useAccordion = true;
    // Abrir/cerrar una fila. Registra su FORMA (posición + tipo de acción) para poder reencontrarla
    // si una acción del usuario la transforma — al vincular, un CREATE pasa a ser un UPDATE de otra
    // BU y cambia de identidad. Mientras la identidad sobreviva, no hace falta nada de esto.
    const toggleRow = (item, index) => {
        const identity = rowIdentity(item);
        // La forma se decide DENTRO del functional update, con el estado de verdad: leerla del
        // closure la hacía decidir sobre el valor capturado en el render, que con dos toggles
        // rápidos (o un re-render en el medio) puede estar viejo.
        setExpandedIdentity((cur) => {
            const cerrando = cur === identity;
            expandedShapeRef.current = cerrando ? null : {index, actionType: item.action_type};
            return cerrando ? null : identity;
        });
    };

    // Deja anotado dónde está en pantalla la fila que el usuario está trabajando, JUSTO ANTES de
    // disparar una acción que regenera la propuesta. Lo que se responde desaparece de la lista, así
    // que el contenido de arriba se acorta y todo lo demás sube: sin esto, la fila se te escapa del
    // ojo aunque siga abierta.
    const anchorExpandedRow = () => {
        const node = expandedIdentity ? rowNodes.current[expandedIdentity] : null;
        pendingAnchor.current = node && typeof node.getBoundingClientRect === 'function'
            ? {identity: expandedIdentity, top: node.getBoundingClientRect().top} : null;
    };

    // …y se la devuelve al mismo lugar del viewport una vez redibujada. Va en `useLayoutEffect`
    // (antes del paint) para que el salto no llegue a verse.
    useLayoutEffect(() => {
        const anchor = pendingAnchor.current;
        if (!anchor) return;
        pendingAnchor.current = null;
        const node = rowNodes.current[anchor.identity];
        if (!node || typeof node.getBoundingClientRect !== 'function') return;
        const delta = node.getBoundingClientRect().top - anchor.top;
        if (!delta) return;
        const container = scrollableAncestor(node);
        if (container) container.scrollTop += delta;
        else if (typeof window.scrollBy === 'function') window.scrollBy(0, delta);
    }, [proposal]);

    const load = useCallback(() => {
        return MediaPlanApi.getProposal(clientId, connection.id).then((data) => {
            if (data) setProposal(data.proposal || null);
        });
    }, [clientId, connection.id]);

    // Mensaje de error REAL del backend. `APIClient` no rechaza en 4xx: resuelve con
    // `{status:'ERROR', error_message}` (mapea `detail`/`message`/`error_message` del body) — nunca
    // `error`. Leer `data.error` dejaba TODOS los 400 accionables del media plan ("Esta línea ya fue
    // resuelta", "Identificá las N campañas…") tapados por el genérico (2026-07-31).
    // Sin red no hay mensaje del backend: el `error_message` que arma `APIClient` es un texto fijo en
    // voseo, así que va el copy propio en el idioma de la pantalla (review C-mp-fe-12).
    const apiError = (data) => (isNetworkError(data) ? null : (data && (data.error_message || data.error)))
        || t(`${NS}.toast.error`);

    // Una acción de escritura que falla puede haberse aplicado igual en el servidor (respuesta
    // perdida, timeout, SSL del dev server): la propuesta local queda VIEJA y la acción siguiente
    // muere con "esta línea ya fue resuelta". Recargar la vigente deja la pantalla en sincronía.
    const failAndResync = (data) => {
        toast.error(apiError(data));
        load().catch(() => {});
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        MediaPlanApi.getProposal(clientId, connection.id)
            .then((data) => active && setProposal((data && data.proposal) || null))
            .catch(() => active && toast.error(t(`${NS}.toast.error`)))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
        // `last_synced_at` en las deps: cuando la primera lectura async termina (el polling de las
        // conexiones lo detecta), la propuesta recién generada se carga sola — sin F5.
    }, [clientId, connection.id, connection.last_synced_at, t]);

    // Sube al contenedor la "fuente" de esta propuesta → el strip la muestra junto a la planilla:
    // la solapa leída Y la fecha del último cambio detectado (`snapshot.created_on` — el snapshot
    // solo se regenera cuando el contenido cambió, así que ES la fecha del último cambio; distinta
    // de `last_synced_at`, que solo dice "Dashbo verificó"). `onSnapshotInfo` es un método estable
    // del contenedor (no re-crea el efecto).
    useEffect(() => {
        if (!onSnapshotInfo) return;
        // Sólo `readAt`: la SOLAPA ya no sale de acá — es propiedad de la conexión (`tab_scan`) y
        // viaja con ella, para que el strip pueda mostrarla incluso sin propuesta.
        onSnapshotInfo(proposal && proposal.snapshot
            ? {readAt: proposal.snapshot.created_on}
            : null);
    }, [proposal, onSnapshotInfo]);

    // Sube al contenedor el resumen para el encabezado de tarea (2.1): cuántos ítems piden revisión
    // y cuántos ofrecen vincular una línea existente. Prop opcional y estable en el contenedor
    // (método de clase), igual que `onSnapshotInfo`; sin callback no se computa nada.
    useEffect(() => {
        if (!onSummaryChanged) return;
        onSummaryChanged(proposalSummary(proposal));
    }, [proposal, onSummaryChanged]);

    // Métricas custom evaluables como objetivo, para el MetricCombobox — mismo patrón (y mismas
    // métricas) que GoalConfiguration en el editor de la línea.
    useEffect(() => {
        fetchCustomMetrics().then((items) => setCustomMetrics(filterForGoals(items)));
    }, []);

    // Acordeón UX 2.1: abrir la 1ra fila que pide acción (conflicto > identificar > revisar) cuando
    // NO hay ninguna fila abierta que sobreviva. Con la identidad como fuente de verdad, una
    // regeneración normal ya no pasa por acá: la fila se dibuja abierta sola y este efecto sólo
    // confirma. Queda para tres casos — el arranque, la línea que cambió de forma al vincular, y la
    // propuesta nueva sin foco previo. Aprobar/rechazar no regenera (update in-place).
    useEffect(() => {
        if (!useAccordion || !proposal) return;
        const rows = (proposal.items || []).filter((i) => i.action_type !== 'NEEDS_DECISION');
        const decision = resolveExpandedIdentity({
            rows,
            expandedIdentity,
            shape: expandedShapeRef.current,
            sameProposal: autoOpenedProposalId.current === proposal.id,
            priorityOf: rowActionPriority,
        });
        autoOpenedProposalId.current = proposal.id;
        if (!decision) return;
        setExpandedIdentity(decision.identity);
        expandedShapeRef.current = decision.shape;
    }, [useAccordion, proposal, expandedIdentity]);

    // `confirmLoss`: avisar antes si hay aprobados sin aplicar. Por defecto sólo al forzar; el «Volver a
    // leer» de la lectura mejorada no fuerza (el endpoint ya regenera sin IA con `regenerate_unchanged`)
    // pero también descarta esas aprobaciones, así que lo pide explícito.
    const generate = (force, confirmLoss = force) => {
        // Re-leer marca STALE la propuesta abierta y sus aprobaciones SIN APLICAR (contrato de
        // parse_service con regenerate_unchanged): si hay trabajo de revisión pendiente, avisar
        // antes — que la pérdida sea una decisión, no una sorpresa (MEDIO-2 del review externo).
        const unappliedApprovals = confirmLoss && proposal
            ? (proposal.items || []).filter((item) => item.review_status === 'APPROVED').length
            : 0;
        if (unappliedApprovals > 0
            && !window.confirm(t(`${NS}.regenerate_confirm`, {count: unappliedApprovals}))) {
            return;
        }
        setGenerating(true);
        MediaPlanApi.generate(clientId, connection.id, force)
            .then((data) => {
                if (!data || data.status !== 'OK') return failAndResync(data);
                setProposal(data.proposal || null);
                // El escaneo llega en la MISMA respuesta: antes `data.parse` se descartaba entero y
                // con él el motivo de que no hubiera propuesta.
                const reason = recordScan(data.tab_scan, !!data.proposal);
                if (data.proposal) {
                    toast.success(t(`${NS}.toast.generated`));
                    return null;
                }
                // NUNCA decir "listo" cuando no se produjo nada: era el aviso que hacía que el
                // usuario clickeara de nuevo esperando otro resultado.
                toast.info(reason
                    ? t(`media_plan.connections.tab.reason.${reason.code}`,
                        {defaultValue: t(`${NS}.toast.no_proposal`)})
                    : t(`${NS}.toast.no_proposal`));
                return null;
            })
            .catch(() => failAndResync(null))
            .finally(() => setGenerating(false));
    };

    // Valor sugerido de un goal degradado por competencia objetivo-vs-real cuando el usuario NO tocó
    // el radio A/B: el 1er candidato (OBJETIVO, ya ordenado por el backend), redondeado. D11: el
    // sistema sugiere, el humano confirma al aprobar. null si el goal no es de esa clase.
    const suggestedGoalValue = (g) =>
        (g.value == null && (g.candidates || []).length > 0) ? roundGoalValue(g.candidates[0].value) : null;

    // Arma el patch de edicion inline (presupuesto + objetivos + tramos) que el backend mergea
    // sobre el payload existente. null si no hubo ediciones. Los objetivos se editan con los
    // componentes canónicos del editor de BU (2026-07-30): valor + métrica (combobox), switch para
    // apagar uno (se cae del array → el apply no lo configura) y fila extra "Objetivo #2".
    const buildEditPatch = (item) => {
        const patch = {};
        // Nombre de la línea (card zMQIrxFe): sólo viaja si el usuario lo cambió y quedó algo. Es su
        // única forma de aislar lo suyo cuando la cuenta mezcla las campañas de la agencia con las de
        // los proveedores del cliente — el nombre es lo que después filtra en el tablero. El backend
        // mergea el patch sobre el payload y el apply crea la BU con ese nombre.
        const editedName = editedNames[rowIdentity(item)];
        if (editedName !== undefined) {
            const trimmed = editedName.trim().slice(0, NAME_MAX_LENGTH);
            if (trimmed && trimmed !== ((item.payload || {}).name || '')) patch.name = trimmed;
        }
        // El presupuesto de la LÍNEA sólo viaja para los items que lo gobiernan. `buildEditPatch`
        // es agnóstico al tipo, así que sin este gate un valor tipeado en un item que no lo
        // muestra —o que dejó de mostrarlo— igual se colaba en el approve.
        const editedBudget = editedBudgets[item.id];
        if (editedBudget !== undefined && editedBudget !== ''
                && item.action_type !== 'UPDATE_CAMPAIGN_BUDGETS') {
            patch.allocated_budget = parseInt(editedBudget, 10);
        }
        const goalEdits = editedGoals[item.id] || {};
        const metricEdits = editedGoalMetrics[item.id] || {};
        const offGoals = disabledGoals[item.id] || {};
        const goals = item.payload.goals || [];
        if (goals.length > 0) {
            let changed = false;
            const patched = [];
            goals.forEach((g, i) => {
                if (offGoals[i]) {
                    changed = true;   // apagado con el switch → no se configura
                    return;
                }
                let value = g.value;
                if (goalEdits[i] !== undefined) {
                    value = goalEdits[i] === '' ? null : parseFloat(goalEdits[i]);
                } else {
                    // Sugerido por el radio A/B si no se tocó, o el del plan intacto.
                    const suggested = suggestedGoalValue(g);
                    if (suggested != null) value = suggested;
                }
                const metric = metricEdits[i] !== undefined ? metricEdits[i] : g.metric_name;
                if (value !== g.value || metric !== g.metric_name) changed = true;
                patched.push({...g, metric_name: metric, value});
            });
            // Fila "Objetivo #2" agregada a mano (solo cuenta con métrica Y valor completos).
            const extra = addedGoals[item.id];
            if (extra && extra.enabled && extra.metricName && extra.value) {
                patched.push({metric_name: extra.metricName, value: parseFloat(extra.value)});
                changed = true;
            }
            // Solo mandar goals si algo cambió respecto del plan (evita un PATCH inútil).
            if (changed) patch.goals = patched;
        }
        // Tramos editados en el configurador REAL (`BudgetPartialGoalsConfiguration`): las filas
        // vuelven al formato del backend ('YYYYMMDD') con el mismo helper que usa el alta de BU.
        // Igual que con los goals, solo se mandan si DIFIEREN de los del plan: el configurador
        // emite `onConfigurationChange` en cualquier interacción (incluido prender y apagar el
        // switch, que devuelve []), y un PATCH que repite el plan es ruido.
        const pgEdit = editedPartialGoals[item.id];
        if (pgEdit) {
            const rows = (pgEdit.budgetPartialGoals || [])
                .filter((r) => r.date && r.value !== '' && r.value != null);
            const submitted = BudgetHelper.BudgetPartialGoalTranslateToSubmit(rows)
                .map((r) => ({...r, value: Number(r.value)}));
            const planGoals = (item.payload.budget_partial_goals || [])
                .map((pg) => ({date: String(pg.date), value: Number(pg.value),
                    type: pg.type || 'PERCENTAGE_GOAL'}));
            const same = submitted.length === planGoals.length
                && submitted.every((r, i) => r.date === planGoals[i].date
                    && r.value === planGoals[i].value && r.type === planGoals[i].type);
            if (!same) patch.budget_partial_goals = submitted;
        }
        return Object.keys(patch).length ? patch : null;
    };

    // Nombre efectivo de una línea: el que el usuario escribió, o el que propuso el plan.
    const effectiveName = (item) => {
        const edited = editedNames[rowIdentity(item)];
        return edited !== undefined ? edited : ((item.payload || {}).name || '');
    };
    // Vaciar el campo NO es "dejá el del plan": es un nombre que el backend va a rechazar al aplicar.
    // Se bloquea la aprobación acá para que se vea al escribir y no doce pasos después.
    //
    // Mira el valor EFECTIVO, no sólo lo que el usuario tocó: si el plan derivó un nombre vacío o de
    // puros espacios, el campo ya se pintaba en rojo pero Aprobar quedaba habilitado, el patch no
    // llevaba `name` —así que la validación del backend ni se disparaba— y el error resucitaba
    // recién en el `AddBusinessUnit`, que es de donde lo vinimos a sacar.
    const nameIsBlank = (item) => item.action_type === 'CREATE_BU' && !effectiveName(item).trim();

    const review = (item, action) => {
        setBusyItemId(item.id);
        // Aprobar no regenera (update in-place), pero igual recarga y la fila cambia de alto: el
        // chip pasa a "Aprobada" y los botones del header se van. Anclarla la deja quieta.
        anchorExpandedRow();
        const payload = action === 'approve' ? buildEditPatch(item) : null;
        MediaPlanApi.reviewItem(item.id, clientId, action, payload)
            .then((data) => {
                // El motivo REAL del rechazo, no un genérico: el backend contesta cosas accionables
                // («el nombre no puede quedar vacío», «identificá las N campañas») y perderlas deja
                // al usuario mirando un "ocurrió un error" que no dice qué hacer.
                if (!data || data.status !== 'OK') toast.error(apiError(data));
                return load();
            })
            .catch(() => failAndResync(null))
            .finally(() => setBusyItemId(null));
    };

    const answer = (item, answerKey, answerText = null) => {
        setBusyItemId(item.id);
        anchorExpandedRow();
        const request = answerText === null
            ? MediaPlanApi.answerItem(item.id, clientId, answerKey)
            : MediaPlanApi.answerItem(item.id, clientId, answerKey, answerText);
        request
            .then((data) => {
                if (data && data.status === 'OK') {
                    setProposal(data.proposal || null);
                    toast.success(t(answerKey === DEFER_KEY
                        ? `${NS}.toast.deferred` : `${NS}.toast.answered`));
                } else {
                    // Ej. la vinculación falló por límite de suscripción → mostrar el mensaje real.
                    failAndResync(data);
                }
            })
            .catch(() => failAndResync(null))
            .finally(() => setBusyItemId(null));
    };

    // Resuelve un aviso de conflicto de objetivo (CONFLICT · GOAL_MISMATCH): el humano elige en el radio
    // A/B "usar el del plan" / "mantener el mío". El backend aplica la decisión y realinea el baseline
    // (no regenera) → recargamos la propuesta vigente y el aviso resuelto sale de los pendientes.
    const resolveConflict = (item, resolution) => {
        setBusyItemId(item.id);
        anchorExpandedRow();
        MediaPlanApi.resolveConflict(item.id, clientId, resolution)
            .then((data) => {
                if (data && data.status === 'OK') {
                    toast.success(t(`${NS}.goal_conflict.toast_resolved`));
                    return load();
                }
                failAndResync(data);
            })
            .catch(() => failAndResync(null))
            .finally(() => setBusyItemId(null));
    };

    // Confirma en lote todas las campañas SUGERIDAS (match con confianza) de una: un POST, una sola
    // regeneración (el de a una regenera tras cada respuesta). Las "sin coincidencia clara" NO entran
    // — se revisan a mano. `suggestedAnswers` se recalcula en el render sobre las dudas vigentes.
    const confirmSuggested = (suggestedAnswers) => {
        if (!proposal || suggestedAnswers.length === 0) return;
        setConfirmingSuggested(true);
        anchorExpandedRow();
        MediaPlanApi.bulkAnswer(proposal.id, clientId, suggestedAnswers)
            .then((data) => {
                if (data && data.status === 'OK') {
                    setProposal(data.proposal || null);
                    toast.success(t(`${NS}.toast.bulk_confirmed`, {count: data.confirmed}));
                } else {
                    failAndResync(data);
                }
            })
            .catch(() => failAndResync(null))
            .finally(() => setConfirmingSuggested(false));
    };

    // Pospone en lote TODAS las dudas de identificación activas (2026-07-23): un POST bulk con
    // answer 'defer' por duda, UNA sola regeneración — antes posponer N eran N clicks con N
    // regeneraciones. Las pospuestas cuentan como resueltas para el gate de la línea.
    const deferRemaining = (deferrableAnswers) => {
        if (!proposal || deferrableAnswers.length === 0) return;
        setDeferringRemaining(true);
        anchorExpandedRow();
        MediaPlanApi.bulkAnswer(proposal.id, clientId, deferrableAnswers)
            .then((data) => {
                if (data && data.status === 'OK') {
                    setProposal(data.proposal || null);
                    toast.success(t(`${NS}.toast.bulk_deferred`, {count: deferrableAnswers.length}));
                } else {
                    failAndResync(data);
                }
            })
            .catch(() => failAndResync(null))
            .finally(() => setDeferringRemaining(false));
    };

    // Confirma la decisión de destino de UNA card de CREATE con candidatas (vincular con una BU o
    // crear nueva). Reusa el mismo answer endpoint de las dudas: pinnea el destino y regenera la
    // propuesta (la card renace mostrando el diff real contra la BU vinculada, o la crea nueva).
    // Si el usuario aceptó el default SIN tocar el radio (caso más común: una sola sugerida
    // pre-marcada), `linkChoices[item.id]` está vacío → cae al mismo default que el render.
    const confirmLink = (item) => {
        const answerKey = linkChoices[item.id] !== undefined
            ? linkChoices[item.id] : defaultLinkAnswerKey(item);
        if (!answerKey) return;
        answer(item, answerKey);
    };

    // Vincula en lote todas las cards con una candidata SUGERIDA (una por card): un POST bulk, una
    // sola regeneración (mismo flujo que "Confirmar N sugeridas" de las dudas de alias).
    const linkSuggested = (suggestedLinks) => {
        if (!proposal || suggestedLinks.length === 0) return;
        setLinkingSuggested(true);
        anchorExpandedRow();
        MediaPlanApi.bulkAnswer(proposal.id, clientId, suggestedLinks)
            .then((data) => {
                if (data && data.status === 'OK') {
                    setProposal(data.proposal || null);
                    toast.success(t(`${NS}.toast.linked_suggested`, {count: data.confirmed}));
                } else {
                    failAndResync(data);
                }
            })
            .catch(() => failAndResync(null))
            .finally(() => setLinkingSuggested(false));
    };

    // P1: deshace la DECISIÓN de destino de una línea (pin `bu_for::<line_key>`) y regenera. No
    // revierte cambios ya aplicados (el confirm inline lo avisa). El foco se preserva seteando la
    // identidad estable ANTES del POST: la línea renace con el MISMO line_key (link⇄create) y el
    // efecto de auto-open la reencuentra por `rowIdentity`.
    const undoLink = (lineKey) => {
        if (!lineKey || undoingLink) return;
        setUndoingLink(true);
        anchorExpandedRow();
        // La línea renace con el MISMO `line_key` (link ⇄ create), así que fijar su identidad ahora
        // hace que la propuesta nueva la dibuje abierta de entrada — incluso cuando el undo se pide
        // desde el resumen "al día", donde esa línea todavía no tenía fila propia.
        //
        // La identidad se pide al HELPER y no se escribe a mano: cuando `rowIdentity` sumó el
        // `action_type` al final, este literal dejó de matchear con ninguna fila y el acordeón caía
        // a "la primera accionable" — justo lo que esta línea existe para evitar. Un literal acá no
        // lo ata nada al helper; la llamada sí.
        setExpandedIdentity(rowIdentity({action_type: 'CREATE_BU', payload: {line_key: lineKey}}));
        expandedShapeRef.current = null;
        MediaPlanApi.undoLink(clientId, connection.id, lineKey)
            .then((data) => {
                if (data && data.status === 'OK') {
                    setProposal(data.proposal || null);
                    toast.success(t(`${NS}.toast.link_undone`));
                } else {
                    failAndResync(data);
                }
            })
            .catch(() => failAndResync(null))
            .finally(() => {
                setUndoingLink(false);
                setUndoTarget(null);
            });
    };

    // UD-2: fija la moneda del plan (o vuelve a la automática con `null`) y regenera sin IA. La usan el
    // «Cambiar» del bloque de moneda, la pregunta cuando el backend no pudo decidirla y la salida del
    // conflicto de moneda («El plan está en COP»).
    const changePlanCurrency = (code) => {
        if (savingCurrency) return;
        setSavingCurrency(true);
        anchorExpandedRow();
        MediaPlanApi.setPlanCurrency(clientId, connection.id, code)
            .then((data) => {
                if (data && data.status === 'OK') {
                    setProposal(data.proposal || null);
                    setCurrencyPickerOpen(false);
                    toast.success(code
                        ? t(`${NS}.toast.currency_changed`, {code})
                        : t(`${NS}.toast.currency_auto`));
                } else {
                    failAndResync(data);
                }
            })
            .catch(() => failAndResync(null))
            .finally(() => setSavingCurrency(false));
    };

    // Base de los montos de UNA tabla (pin `basis_for::<firma>`): regenera sin IA. El error se dice con
    // texto propio, nunca con el `error_message` del backend.
    const changePlanBasis = (signature, column) => {
        if (savingBasis !== null) return;
        setSavingBasis(signature);
        anchorExpandedRow();
        const fail = () => {
            toast.error(t('media_plan.basis.toast_error'));
            load().catch(() => {});
        };
        MediaPlanApi.setPlanBasis(clientId, connection.id, signature, column)
            .then((data) => {
                if (!data || data.status !== 'OK') return fail();
                setProposal(data.proposal || null);
                setBasisPickerFor(null);
                toast.success(t('media_plan.basis.toast_saved'));
                return null;
            })
            .catch(fail)
            .finally(() => setSavingBasis(null));
    };

    // Guarda el reparto de TODOS los grupos visibles con destino (también los pre-elegidos que no se
    // tocaron: guardar es lo que confirma la sugerencia) y regenera sin IA. Con `dismiss` («Seguir como
    // hoy», ronda 2b) los guarda todos «como hoy»: descarta las sugerencias. `savingMapping` dice qué botón
    // está guardando ('save' / 'dismiss').
    const saveGroupMapping = (dismiss = false) => {
        if (savingMapping || !proposal) return;
        const {groups, lines, suggestedNotice} = groupMappingState(proposal);
        const mapping = dismiss
            ? dismissPayload(groups)
            : mappingPayload(groups, effectiveChoices(groups, lines, mappingEdits, suggestedNotice));
        if (Object.keys(mapping).length === 0) return;
        setSavingMapping(dismiss ? 'dismiss' : 'save');
        anchorExpandedRow();
        const fail = () => {
            toast.error(t('media_plan.group_mapping.toast_error'));
            load().catch(() => {});
        };
        MediaPlanApi.saveGroupMapping(clientId, connection.id, mapping)
            .then((data) => {
                if (!data || data.status !== 'OK') return fail();
                setProposal(data.proposal || null);
                setMappingEdits({});
                toast.success(t('media_plan.group_mapping.toast_saved'));
                return null;
            })
            .catch(fail)
            .finally(() => setSavingMapping(null));
    };

    // ¿Está abierta la confirmación de undo para esta línea? (`where` desambigua cuando la misma
    // línea aparece en la franja de la card Y en el resumen "al día").
    const isConfirmingUndo = (lineKey, where) =>
        !!undoTarget && undoTarget.lineKey === lineKey && undoTarget.where === where;

    // Confirmación del undo: patrón de dos pasos SIN modal y sin caja anidada — la fila de la franja
    // se REEMPLAZA por la confirmación (misma caja, mismo alto), así no aparece un panel blanco
    // flotando dentro de otro (feedback fede 2026-07-31). Mientras guarda, el botón se reemplaza por
    // el `SpinnerButton` canónico en vez de teñirse de naranja apagado (que no es un patrón de Dashbo).
    const renderUndoConfirmRow = (lineKey) => (
        <div className="mp-link-note-row -confirm">
            <FaIcon iconName="triangle-exclamation" className="mp-link-note-icon -warn"/>
            <span className="mp-link-note-text">{t(`${NS}.link_note.confirm_text`)}</span>
            {undoingLink ? <SpinnerButton/> : (
                <>
                    <button type="button" className="btn -alt -negative mp-btn-sm"
                            onClick={() => undoLink(lineKey)}>
                        {t(`${NS}.link_note.confirm_cta`)}
                    </button>
                    <button type="button" className="mp-link-note-cancel"
                            onClick={() => setUndoTarget(null)}>
                        {t(`${NS}.link_note.cancel`)}
                    </button>
                </>
            )}
        </div>
    );

    const apply = () => {
        setApplying(true);
        MediaPlanApi.apply(proposal.id, clientId)
            .then((data) => {
                if (data && data.status === 'OK') {
                    setProposal(data.proposal || null);
                    const {applied, failed} = data.summary;
                    // F2: la BU se creó pero los budgets por campaña no se pudieron configurar.
                    const configWarnings = applied.filter(
                        (i) => (i.created_entity_ids || {}).campaign_config_warning).length;
                    // Flight: la BU se creó pero los tramos de pacing intra-mes no se aplicaron
                    // (módulo de suscripción OFF / fecha fuera del período) → warning propio.
                    const partialGoalWarnings = applied.filter(
                        (i) => (i.created_entity_ids || {}).partial_goals_config_warning).length;
                    if (failed.length === 0) {
                        toast.success(t(`${NS}.toast.applied`, {count: applied.length}));
                    } else {
                        toast.warn(t(`${NS}.toast.applied_partial`,
                            {ok: applied.length, failed: failed.length}));
                    }
                    if (configWarnings > 0) {
                        toast.warn(t(`${NS}.toast.campaign_config_warning`, {count: configWarnings}));
                    }
                    if (partialGoalWarnings > 0) {
                        toast.warn(t(`${NS}.toast.partial_goals_config_warning`, {count: partialGoalWarnings}));
                    }
                } else {
                    // Ej. el gate de asignación (los objetivos requieren el cliente asignado al
                    // usuario que aplica) → mostrar el mensaje real y accionable del backend.
                    failAndResync(data);
                }
            })
            .catch(() => failAndResync(null))
            .finally(() => setApplying(false));
    };

    if (loading) {
        return (
            <div className="box-loader">
                <Spinner color="positive"/>
            </div>
        );
    }

    const items = proposal ? proposal.items : [];
    const changes = items.filter((i) => i.action_type !== 'NEEDS_DECISION');
    // Moneda del plan (UD-2). `{}` en propuestas anteriores al cambio: sin bloque, como antes.
    const {planCurrency, hasPlanCurrency, needsCurrency} = planCurrencyState(proposal);
    // Base de los montos y reparto de grupos (tanda «grupos, dinero y DV360»). Sin sus claves en la
    // propuesta (backend viejo o agencia sin el flag) no hay bloque ni bloqueo: la pantalla de siempre.
    const basis = planBasisState(proposal);
    const basisAsk = basis.ask;   // una tabla sin base decidida: no se aprueba nada hasta elegirla
    const mapping = groupMappingState(proposal);
    const mappingChoices = effectiveChoices(mapping.groups, mapping.lines, mappingEdits, mapping.suggestedNotice);
    const decisionGroups = partitionProposalQuestions(items);
    // Sólo las dudas de alias alimentan el progreso, los grupos y los bulk de campañas.
    const questions = decisionGroups.campaign;
    const questionGroups = groupQuestions(questions);
    // Las dudas de base y de reparto se responden en sus bloques; si el bloque no vino (contrato a
    // medias), no se pierden: quedan como aviso.
    const blockingNotes = [...decisionGroups.notice,
        ...(basis.hasPlanBasis ? [] : decisionGroups.basis),
        ...(mapping.hasGroupMapping ? [] : decisionGroups.group_mapping)];
    // Moneda conserva su control propio; propuestas antiguas sin metadata muestran sus opciones.
    const planDecisionKinds = Object.keys(PLAN_DECISION_COPY)
        .filter((kind) => kind !== 'currency' || !hasPlanCurrency);
    const planDecisionCount = planDecisionKinds.reduce((count, kind) => count + decisionGroups[kind].length, 0);
    // "Confirmar N sugeridas" / "Posponer las N": los bulk se computan por-ámbito (cada card
    // gateada las suyas inline; el acordeón las huérfanas) — ver `cardSuggested`/`cardDeferrable`
    // en `renderItem` y `accordionSuggested`/`accordionDeferrable` abajo.
    // Identificación INLINE (2026-07-24): las dudas de una card gateada PENDIENTE se resuelven
    // DENTRO de su card (mismo lugar donde el usuario decide aprobarla), no en un acordeón al pie
    // — el acordeón separado era un vestigio de cuando identificar era "opcional"; con el gate, es
    // parte de resolver cada línea. El acordeón queda SOLO para alias huérfanos (modo clásico,
    // 1 BU sin split, no gateada); las decisiones del plan y de cuenta tienen su propio bloque.
    const inlineGroupKeys = new Set(
        changes.filter((i) => isGatedItem(i) && i.review_status === 'PENDING_REVIEW')
            .map((i) => {
                const a = ((i.payload || {}).accounts || [])[0] || {};
                return questionGroupKey(a.platform, a.ad_account_name);
            }));
    const accordionGroups = questionGroups.filter((g) => !inlineGroupKeys.has(g.key));
    const accordionActiveQuestions = accordionGroups
        .flatMap((g) => g.questions).filter((q) => !(q.question || {}).deferred);
    const accordionSuggested = accordionActiveQuestions
        .map((q) => ({q, option: suggestedAliasOption(q.question)}))
        .filter((x) => x.option)
        .map((x) => ({item_id: x.q.id, answer_key: x.option.key}));
    const accordionDeferrable = accordionActiveQuestions
        .filter((q) => (q.question || {}).alias_pin_key
            && ((q.question || {}).options || []).some((o) => o.key === DEFER_KEY))
        .map((q) => ({item_id: q.id, answer_key: DEFER_KEY}));
    // Avisos de la LECTURA del plan (snapshot): tramos/objetivos que no se pudieron verificar contra
    // la planilla → recall real para el HITL. Allowlist por tipo (SNAPSHOT_NOTE_TYPES): deja afuera
    // el ruido de celdas con error del grid (#REF!/#DIV/0!) y los warnings de type libre del LLM.
    const snapshotNotes = ((proposal && proposal.snapshot && proposal.snapshot.warnings) || [])
        .filter((w) => w && w.note && SNAPSHOT_NOTE_TYPES.has(w.type));
    // Tal como vienen del conciliador: `REANCHORED` uno por tabla (con todas sus celdas) y `ROW_NOT_READ`
    // uno por campaña, con su nombre (sin nombre no hay qué decir de él). `PLATFORM_NOT_TRACKED` se dice con su
    // plataforma y su plata: sin alguna de las dos no hay aviso (el `note` del backend no se muestra crudo).
    const proposalWarnings = ((proposal && proposal.warnings) || []).filter((w) => {
        if (!w || !PROPOSAL_WARNING_TYPES.includes(w.type)) return false;
        if (w.type === 'PLATFORM_NOT_TRACKED') {
            return !!String(w.platform || '').trim() && w.amount !== null && w.amount !== undefined
                && w.amount !== '' && Number.isFinite(Number(w.amount));
        }
        return (w.cells || []).length > 0 && (w.type !== 'ROW_NOT_READ' || w.name);
    });
    const warningCellsText = (cells) => {
        const shown = cells.slice(0, WARNING_CELLS_SHOWN).join(', ');
        return cells.length > WARNING_CELLS_SHOWN
            ? t('media_plan.warnings.more_cells', {cells: shown, count: cells.length - WARNING_CELLS_SHOWN,
                interpolation: {escapeValue: false}})
            : shown;
    };
    // Encabezado de plataforma SOLO cuando esa plataforma tiene ≥2 cards (el par global + campañas):
    // agrupa las dos líneas de un canal para que se lean como una unidad (evita aprobar una y
    // olvidar la otra). Una card suelta (modo clásico) no lleva encabezado.
    const platformCardCounts = changes.reduce((acc, item) => {
        const plat = cardPlatform(item);
        if (plat) acc[plat] = (acc[plat] || 0) + 1;
        return acc;
    }, {});
    // Cards de CREATE con candidatas de vinculación PENDIENTES: mientras no se decide el destino, la
    // aprobación queda bloqueada (la decisión de destino precede a la aprobación). Cada card con una
    // candidata `suggested` alimenta el bulk "Vincular las N sugeridas" (answer_key = link::<bu_id>).
    const suggestedLinks = changes
        .filter((i) => i.action_type === 'CREATE_BU' && i.review_status === 'PENDING_REVIEW')
        .map((i) => ({item: i, candidate: suggestedLinkCandidate(i)}))
        .filter((x) => x.candidate)
        .map((x) => ({item_id: x.item.id, answer_key: `link::${x.candidate.bu_id}`}));
    // Líneas ya vinculadas al plan que ya lo reflejan (satisfechas, sin item propio). Puede faltar en
    // propuestas viejas → degrada a resumen idéntico a hoy.
    const linkedUpToDate = (proposal && proposal.linked_up_to_date) || [];
    // Estado del plan POR CAMPAÑA (modelo mental del dueño del plan): de las campañas que el plan
    // detalla con presupuesto/objetivo propios, cuántas pudo Dashbo identificar. `matched` = campañas
    // ya resueltas de la card; `unmatched_campaigns` = las que van al acordeón. Una card por
    // plataforma → sumar sobre las cards da el total del plan sin doble conteo, PERO la BU3 (objetivo
    // global, marcada con `advisory`) re-lista las MISMAS campañas ya contadas en las cards por
    // plataforma → se excluye del conteo (es una lente del resultado global, no campañas nuevas del
    // plan; sin esto el contador dobla, ej. 22/29 en vez de 11/18).
    const planCampaigns = changes.reduce((acc, i) => {
        const p = i.payload || {};
        if (p.advisory) return acc;
        // `additions` = UPDATE_BU_CAMPAIGNS (2026-07-22): campañas ya identificadas que se sumarán
        // a la línea aplicada — cuentan como identificadas del plan. Las pospuestas ("asignar más
        // adelante") cuentan aparte: resuelven el gate pero no son identificadas.
        const matched = (p.accounts || []).reduce((s, a) => s + ((a.campaigns || []).length), 0)
            + (p.additions || []).length;
        const deferred = (p.unmatched_campaigns || []).filter((u) => u.deferred).length;
        const unmatched = (p.unmatched_campaigns || []).length;
        return {identified: acc.identified + matched, deferred: acc.deferred + deferred,
                total: acc.total + matched + unmatched};
    }, {identified: 0, deferred: 0, total: 0});
    const planResolved = planCampaigns.identified + planCampaigns.deferred;
    const approvedCount = changes.filter(
        (i) => i.review_status === 'APPROVED' && APPLIABLE.has(i.action_type)).length;
    // ¿Hay una acción en vuelo que va a REGENERAR la propuesta? Mientras dura, los items que se ven
    // en pantalla ya son viejos: el backend crea PKs nuevas, así que responder una segunda duda con
    // el id que quedó en el DOM muere con "la duda ya fue respondida" — es el "quise asociar
    // haciendo click rápido y ahí falló" del piloto. Antes sólo se bloqueaba la duda clickeada.
    // `applying` también: cambiar la moneda mientras se aplica (o al revés) dejaría aplicar en la
    // moneda anterior (review C-08).
    const regenerating = busyItemId !== null || confirmingSuggested
        || deferringRemaining || linkingSuggested || undoingLink || savingCurrency || applying || interpretationOpen
        || savingBasis !== null || savingMapping !== null
        // «Volver a leer» reemplaza la propuesta entera: lo que se apruebe o aplique mientras tanto se pierde.
        || generating;
    // Las dudas colapsan por defecto para no enterrar los cambios; si todavía no hay cambios, se
    // abren solas (son el único paso disponible).
    const showQuestions = questionsOpen || changes.length === 0;

    // Preview de la celda citada (`CellPeek`): se reconstruye desde el grid del snapshot VIGENTE de
    // esta propuesta, así que solo aplica a evidencia de ESA solapa. Si el gid no coincide (o no hay
    // celda/snapshot), devuelve null y el link queda sin preview — nunca muestra el vecindario de
    // otra solapa, que sería peor que no mostrar nada.
    const snapshot = (proposal && proposal.snapshot) || null;
    const peekCell = (evidence) => {
        if (!snapshot || !evidence || !evidence.cell) return null;
        const sameTab = evidence.gid === null || evidence.gid === undefined
            || snapshot.tab_gid === null || snapshot.tab_gid === undefined
            || String(evidence.gid) === String(snapshot.tab_gid);
        return sameTab ? evidence.cell : null;
    };

    return (
        // El cuerpo de la propuesta ya NO lleva header propio: la planilla (source strip) es el
        // encabezado del panel único y "Releer plan" reemplaza al viejo "Leer el plan y proponer
        // cambios" de acá. El botón de generar vive ahora en `MediaPlanEmptyState`, que lo elige
        // según el motivo por el que no hay propuesta (arrancar la 1ra lectura, releer, etc.).
        <div className="cmem-connections mp-proposal">
            {!proposal ? (
                firstReadRunning ? (
                    // Primera lectura async en curso (recién conectada): estado "procesando", sin
                    // botón — la propuesta se carga sola cuando el polling detecta la lectura.
                    <div className="cmem-empty">
                        <div className="box-loader" style={{minHeight: 'auto', marginBottom: '0.8em'}}>
                            <Spinner color="positive"/>
                        </div>
                        <h3>{t(`${NS}.first_read_title`)}</h3>
                        <p>{t(`${NS}.first_read_subtitle`)}</p>
                    </div>
                ) : (
                    // Empty state POR MOTIVO: dice qué solapa se leyó, por qué no sirvió y qué
                    // hacer. Antes era una sola pantalla muda con un botón que no podía recuperar.
                    <MediaPlanEmptyState
                        scan={effectiveScan}
                        externalId={connection.external_id}
                        generating={generating}
                        onGenerate={() => generate(false)}
                        onReread={() => generate(true)}
                        onPickTab={onPickTab}
                        onUseTab={onPickTab ? (candidate) => onPickTab(candidate.gid) : null}
                        repeatedFailure={repeatedFailure}
                    />
                )
            ) : (
                <>
                    {/* Lectura mejorada (§8 del contrato): el sync no regenera una propuesta con
                        aprobaciones o correcciones, así que se ofrece acá. Sin decisiones no se muestra:
                        esa la regenera sola el sync. Azul: informa, no bloquea. */}
                    {readingUpgradeVisible(proposal) && (
                        <div className="mp-reading-upgrade" data-testid="mp-reading-upgrade">
                            <FaIcon iconName="info-circle" className="icon"/>
                            <div className="mp-reading-upgrade-body">
                                <span className="mp-reading-upgrade-title">{t('media_plan.reading_upgrade.title')}</span>
                                <div className="mp-reading-upgrade-hint">{t('media_plan.reading_upgrade.hint')}</div>
                            </div>
                            <button type="button" className="btn -alt -negative mp-btn-sm"
                                    disabled={regenerating || generating}
                                    onClick={() => generate(false, true)}>
                                {generating ? t(`${NS}.generating`) : t('media_plan.reading_upgrade.action')}
                            </button>
                        </div>
                    )}
                    <div className="mp-reading-entry">
                        <button type="button" className="btn -negative" disabled={regenerating}
                                onClick={() => setInterpretationOpen(true)}>
                            {t('media_plan.interpretation.title')}
                        </button>
                    </div>
                    {interpretationOpen && <InterpretationReview proposalId={proposal.id} clientId={clientId}
                        externalId={connection.external_id} onClose={() => setInterpretationOpen(false)}
                        onCurrentProposal={setProposal} onReloadProposal={() => {
                            setInterpretationOpen(false);
                            load().catch(() => toast.error(t(`${NS}.toast.error`)));
                        }}
                        onSaved={(response) => {
                            setProposal(response.proposal);
                            setInterpretationOpen(false);
                            toast.success(t('media_plan.interpretation.saved'));
                        }}
                    />}
                    {/* Intro persistente SOLO en propuestas abiertas: cuando ya existe una (ej. la
                        conectó otra persona del equipo), el usuario no vio el empty-state que explica
                        el flujo. En applied/stale/partially el "nada se aplica hasta que aprobás" ya
                        no corresponde → no se muestra. */}
                    {proposal.status === 'OPEN' && (
                        <p className="mp-proposal-intro">{t(`${NS}.intro`)}</p>
                    )}

                    {/* Resumen: orienta de un vistazo (qué hay para hacer + de dónde salió). */}
                    <div className="mp-overview">
                        <div className="mp-overview-stats">
                            <div className="mp-overview-stat">
                                <span className="mp-overview-num">{changes.length}</span>
                                <span className="mp-overview-label">
                                    {t(`${NS}.overview.changes`, {count: changes.length})}
                                </span>
                            </div>
                            {planCampaigns.total > 0 ? (
                                // Progreso del plan por-campaña: comunica el modelo mental del dueño
                                // (su plan es por campaña) y cuánto falta para reflejarlo fielmente.
                                // Sin dudas pendientes el acordeón no existe → stat estático, no un
                                // botón que no hace nada (mismo criterio que los CTAs de las cards).
                                questions.length > 0 ? (
                                    <button type="button" className="mp-overview-stat -clickable"
                                            onClick={() => setQuestionsOpen((open) => !open)}>
                                        <span className={`mp-overview-num ${
                                            planResolved < planCampaigns.total ? '-warn' : '-good'}`}>
                                            {planCampaigns.identified}/{planCampaigns.total}
                                        </span>
                                        <span className="mp-overview-label">
                                            {planCampaigns.deferred > 0
                                                ? t(`${NS}.overview.campaigns_resolved`,
                                                    {count: planCampaigns.deferred})
                                                : t(`${NS}.overview.campaigns_identified`)}
                                        </span>
                                    </button>
                                ) : (
                                    <div className="mp-overview-stat">
                                        <span className={`mp-overview-num ${
                                            planResolved < planCampaigns.total ? '-warn' : '-good'}`}>
                                            {planCampaigns.identified}/{planCampaigns.total}
                                        </span>
                                        <span className="mp-overview-label">
                                            {planCampaigns.deferred > 0
                                                ? t(`${NS}.overview.campaigns_resolved`,
                                                    {count: planCampaigns.deferred})
                                                : t(`${NS}.overview.campaigns_identified`)}
                                        </span>
                                    </div>
                                )
                            ) : questions.length > 0 && (
                                <button type="button" className="mp-overview-stat -clickable"
                                        onClick={() => setQuestionsOpen((open) => !open)}>
                                    <span className="mp-overview-num -warn">{questions.length}</span>
                                    <span className="mp-overview-label">
                                        {t(`${NS}.overview.questions`, {count: questions.length})}
                                    </span>
                                </button>
                            )}
                        </div>
                        {/* De qué solapa salió ESTA propuesta (C6): el strip dice cuál se lee hoy, y
                            puede no ser la de la propuesta si se cambió después. Más el estado de la
                            propuesta cuando no es OPEN (STALE/APLICADA). */}
                        {((proposal.snapshot || {}).tab_title || proposal.status !== 'OPEN') && (
                            <span className="mp-overview-source">
                                {(proposal.snapshot || {}).tab_title && (
                                    <span className="mp-overview-tab" data-testid="mp-overview-tab">
                                        {t('media_plan.connections.tab.summary_tab', {
                                            tab: proposal.snapshot.tab_title,
                                            interpolation: {escapeValue: false},
                                        })}
                                    </span>
                                )}
                                {proposal.status !== 'OPEN' && (
                                    <span className="mp-status-chip">
                                        {t(`${NS}.status.${proposal.status.toLowerCase()}`, {defaultValue: proposal.status})}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>

                    {renderPlanCurrency()}
                    {renderPlanBasis()}
                    {renderGroupMapping()}

                    {planDecisionKinds.map((kind) => renderPlanDecisions(kind, decisionGroups[kind]))}

                    {/* Estado "al día" de las líneas vinculadas al plan que ya lo reflejan (sin item
                        propio → silencio). Franja afirmativa verde (NO warning): confirma que el
                        vínculo funciona. Los nombres colapsan si son muchos. P1: cada entry con
                        `line_key` (propuestas nuevas) es un chip con "Deshacer vínculo" — acá NO
                        existe item, así que este es el ÚNICO lugar donde deshacer esa decisión;
                        entries viejas sin `line_key` degradan a texto plano. */}
                    {linkedUpToDate.length > 0 && (
                        <div className="mp-linked-uptodate">
                            <div className="mp-linked-uptodate-row">
                                <FaIcon iconName="circle-check" className="icon"/>
                                <span className="mp-linked-uptodate-text">
                                    {t(`${NS}.linked_up_to_date`, {count: linkedUpToDate.length})}
                                </span>
                                <span className="mp-linked-uptodate-names">
                                    {linkedUpToDate.slice(0, 3).map((l, li) => (
                                        <span key={li} className="mp-linked-chip">
                                            {l.name}
                                            {l.line_key && (
                                                <button type="button" className="mp-linked-chip-undo"
                                                        disabled={undoingLink}
                                                        onClick={() => setUndoTarget(
                                                            {lineKey: l.line_key, where: 'summary'})}>
                                                    {t(`${NS}.linked_up_to_date_undo`)}
                                                </button>
                                            )}
                                        </span>
                                    ))}
                                    {linkedUpToDate.length > 3
                                        && ` ${t(`${NS}.linked_up_to_date_more`, {count: linkedUpToDate.length - 3})}`}
                                </span>
                            </div>
                            {undoTarget && undoTarget.where === 'summary' && (
                                <div className="mp-link-note -inline">
                                    {renderUndoConfirmRow(undoTarget.lineKey)}
                                </div>
                            )}
                        </div>
                    )}

                    {(blockingNotes.length > 0 || snapshotNotes.length > 0 || proposalWarnings.length > 0) && (
                        <div className="mp-notes">
                            {blockingNotes.map((q) => {
                                const noteUrl = spreadsheetUrl(connection.external_id, q.evidence);
                                return (
                                    <div key={q.id} className="mp-note">
                                        <FaIcon iconName="triangle-exclamation" className="icon"/>
                                        <div className="mp-note-body">
                                            <span>{q.question.question_text}</span>
                                            {noteUrl && (
                                                <CellPeek agencyClientId={clientId}
                                                          snapshotId={snapshot && snapshot.id}
                                                          cell={peekCell(q.evidence)}>
                                                    <a href={noteUrl} target="_blank" rel="noopener noreferrer"
                                                       className="mp-note-link">
                                                        <FaIcon iconName="up-right-from-square"/>
                                                        {t(`${NS}.note_view_sheet`)}
                                                    </a>
                                                </CellPeek>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {/* Avisos de la lectura del plan (objetivos/tramos que no se pudieron
                                verificar). El copy claro vive en el FE por `type`; si el backend manda un
                                type nuevo sin traducir, cae al `note` crudo del backend. */}
                            {snapshotNotes.map((w, i) => (
                                <div key={`snap-note-${i}`} className="mp-note -info">
                                    <FaIcon iconName="triangle-exclamation" className="icon"/>
                                    <div className="mp-note-body">
                                        <span>{t(`${NS}.snapshot_note.${w.type}`, {defaultValue: w.note})}</span>
                                    </div>
                                </div>
                            ))}
                            {proposalWarnings.map((w, i) => (
                                <div key={`warn-${i}`} className="mp-note -info" data-testid={`mp-warning-${w.type}`}>
                                    <FaIcon iconName="info-circle" className="icon"/>
                                    <div className="mp-note-body">
                                        <span>{t(`media_plan.warnings.${w.type}`, {
                                            cells: warningCellsText(w.cells || []), name: w.name || '',
                                            platform: String(w.platform || '').trim(),
                                            amount: formatPlanAmount(w.amount, planCurrency.code || ''),
                                            interpolation: {escapeValue: false},
                                        })}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Atajo: vincular de una todas las líneas que Dashbo sugiere con confianza
                        (candidata `suggested`). Un solo POST bulk + una regeneración. Aparece solo con
                        ≥2 sugeridas (una sola se resuelve cómodo desde su propia card). Reusa el patrón
                        y el estilo del "Confirmar N sugeridas" de las dudas de alias. */}
                    {suggestedLinks.length >= 2 && (
                        <div className="mp-bulk-confirm mp-bulk-link">
                            <button type="button" className="btn -alt"
                                    disabled={linkingSuggested || busyItemId !== null}
                                    onClick={() => linkSuggested(suggestedLinks)}>
                                <FaIcon iconName="link" className="icon"/>
                                {linkingSuggested
                                    ? t(`${NS}.linking_suggested`)
                                    : t(`${NS}.link_suggested`, {count: suggestedLinks.length})}
                            </button>
                            <span className="mp-bulk-confirm-hint">
                                {t(`${NS}.link_suggested_hint`)}
                            </span>
                        </div>
                    )}

                    {changes.length > 0 ? (
                        <>
                        {/* Título de la lista (feedback Pablo 2026-07-29): decir QUÉ son los ítems —
                            líneas de control del tablero. "Línea de control" no aparecía en toda la
                            pantalla y el número de arriba cuenta CAMBIOS (una línea puede traer 2:
                            presupuesto + objetivos), por eso el título no lleva count. */}
                        <div className="mp-list-heading">
                            <h3 className="mp-list-title">{t(`${NS}.changes_title`)}</h3>
                            <p className="mp-list-subtitle">{t(`${NS}.changes_subtitle`)}</p>
                        </div>
                        {useAccordion ? (
                            // UX 2.1: acordeón de filas colapsables (una abierta a la vez). Sin
                            // encabezado de plataforma — cada fila lleva su ícono de canal.
                            <ul className="mp-change-list -accordion">
                                {changes.map((item, i) => renderAccordionRow(item, i))}
                            </ul>
                        ) : (
                        <ul className="mp-change-list">
                            {changes.map((item, i) => {
                                // Encabezado de plataforma al cambiar de canal (run-length sobre el
                                // orden del backend: global y campañas de un canal son adyacentes).
                                const plat = cardPlatform(item);
                                const prevPlat = i > 0 ? cardPlatform(changes[i - 1]) : null;
                                const showHeader = plat && plat !== prevPlat
                                    && platformCardCounts[plat] >= 2;
                                return (
                                    <React.Fragment key={item.id}>
                                        {showHeader && (
                                            <li className="mp-platform-header">
                                                {platformVisual(plat) && (
                                                    <FaIcon prefix={platformVisual(plat).prefix}
                                                            iconName={platformVisual(plat).iconName}
                                                            className="mp-platform-header-icon"
                                                            style={{color: platformVisual(plat).color}}/>
                                                )}
                                                <span className="mp-platform-header-name">
                                                    {PLATFORM_LABEL[plat] || plat}
                                                </span>
                                            </li>
                                        )}
                                        {renderItem(item, i)}
                                    </React.Fragment>
                                );
                            })}
                        </ul>
                        )}
                        </>
                    ) : questions.length === 0 && planDecisionCount === 0 && !needsCurrency
                        && blockingNotes.length === 0 && !basisAsk && !mapping.pending && (
                        <div className="cmem-empty">
                            <FaIcon iconName="circle-check" className="cmem-empty-icon"/>
                            <h3>{t(`${NS}.all_synced_title`)}</h3>
                            <p>{t(`${NS}.all_synced_subtitle`)}</p>
                        </div>
                    )}

                    {accordionGroups.length > 0 && (
                        <section className={`mp-accordion ${showQuestions ? '-open' : ''}`}>
                            <button type="button" className="mp-accordion-head"
                                    onClick={() => setQuestionsOpen((open) => !open)}
                                    disabled={changes.length === 0}>
                                <FaIcon iconName="circle-question" className="mp-accordion-icon"/>
                                <span className="mp-accordion-titles">
                                    <span className="mp-accordion-title">
                                        {/* El título cuenta solo las ACTIVAS del acordeón (las de
                                            las cards gateadas ya se resuelven inline). */}
                                        {accordionActiveQuestions.length > 0
                                            ? t(`${NS}.questions_accordion_title`,
                                                {count: accordionActiveQuestions.length})
                                            : t(`${NS}.questions_accordion_deferred_only`,
                                                {count: accordionGroups.reduce((n, g) => n + g.questions.length, 0)})}
                                    </span>
                                    <span className="mp-accordion-hint">{t(`${NS}.questions_accordion_hint`)}</span>
                                </span>
                                {changes.length > 0 && (
                                    <FaIcon iconName={showQuestions ? 'chevron-up' : 'chevron-down'}
                                            className="mp-accordion-caret"/>
                                )}
                            </button>
                            {showQuestions && (
                                <div className="mp-question-groups">
                                    <p className="mp-questions-instruction">
                                        {t(`${NS}.identify_instruction`)}
                                    </p>
                                    {accordionGroups.map((group) => {
                                        // Pospuestas ("asignar más adelante", 2026-07-22) aparte y
                                        // colapsadas: no molestan, pero elegir ahí la campaña real
                                        // cuando exista las resuelve (el alias levanta el defer).
                                        const activeQs = group.questions.filter((qq) => !(qq.question || {}).deferred);
                                        const deferredQs = group.questions.filter((qq) => (qq.question || {}).deferred);
                                        return (
                                        <div key={group.key}
                                             ref={(el) => { groupRefs.current[group.key] = el; }}
                                             className="mp-qgroup">
                                            {/* Cabecera de la CUENTA: logo del canal (color de marca)
                                                + nombre de la cuenta + descriptor "Cuenta de X Ads"
                                                (antes "Google · Nombre 2" no se entendía qué era, y
                                                el conteo suelto parecía parte del nombre). */}
                                            {(group.platform || group.accountName) && (
                                                <div className="mp-qgroup-head">
                                                    {platformVisual(group.platform) && (
                                                        <FaIcon prefix={platformVisual(group.platform).prefix}
                                                                iconName={platformVisual(group.platform).iconName}
                                                                className="mp-qgroup-channel"
                                                                style={{color: platformVisual(group.platform).color}}/>
                                                    )}
                                                    <span className="mp-qgroup-title">
                                                        {group.accountName
                                                            || PLATFORM_LABEL[group.platform] || group.platform}
                                                    </span>
                                                    {group.platform && group.accountName && (
                                                        <span className="mp-qgroup-kind">
                                                            {t(`${NS}.qgroup_account_kind`, {
                                                                channel: PLATFORM_ADS_LABEL[group.platform]
                                                                    || PLATFORM_LABEL[group.platform] || group.platform,
                                                            })}
                                                        </span>
                                                    )}
                                                    <span className="mp-qgroup-count">
                                                        {t(`${NS}.qgroup_count`, {count: activeQs.length})}
                                                    </span>
                                                </div>
                                            )}
                                            <ul className="mp-question-list">
                                                {activeQs.map((q, i) => renderQuestion(q, i))}
                                            </ul>
                                            {deferredQs.length > 0 && (
                                                <div className="mp-qgroup-deferred">
                                                    <button type="button" className="mp-qgroup-deferred-toggle"
                                                            onClick={() => setDeferredOpen((s) => ({...s, [group.key]: !s[group.key]}))}>
                                                        <FaIcon iconName={deferredOpen[group.key] ? 'chevron-up' : 'chevron-down'}
                                                                className="mp-qgroup-deferred-caret"/>
                                                        {t(`${NS}.deferred_section`, {count: deferredQs.length})}
                                                    </button>
                                                    {deferredOpen[group.key] && (
                                                        <>
                                                            <p className="mp-qgroup-deferred-hint">
                                                                {t(`${NS}.deferred_hint`)}
                                                            </p>
                                                            <ul className="mp-question-list">
                                                                {deferredQs.map((q, i) => renderQuestion(q, i))}
                                                            </ul>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                    {/* Bulk "Confirmar N sugeridas" al PIE: primero se ven las filas
                                        con sus sugerencias, después se confirman todas juntas — el
                                        CTA arriba pedía confirmar algo que aún no se había visto
                                        (feedback fede 2026-07-18). Barra hint-izq / botón-der, espejo
                                        del patrón "Aplicar aprobados". */}
                                    {(accordionSuggested.length > 0 || accordionDeferrable.length > 0) && (
                                        <div className="mp-questions-footer">
                                            <span className="mp-questions-footer-hint">
                                                {accordionSuggested.length > 0
                                                    ? t(`${NS}.confirm_suggested_hint`)
                                                    : t(`${NS}.defer_remaining_hint`)}
                                            </span>
                                            {accordionDeferrable.length > 0 && (
                                                <button type="button" className="btn -alt -negative mp-btn-sm"
                                                        disabled={deferringRemaining || confirmingSuggested}
                                                        onClick={() => deferRemaining(accordionDeferrable)}>
                                                    {deferringRemaining
                                                        ? t(`${NS}.deferring_remaining`)
                                                        : t(`${NS}.defer_remaining`, {count: accordionDeferrable.length})}
                                                </button>
                                            )}
                                            {accordionSuggested.length > 0 && (
                                                <button type="button" className="btn -alt mp-btn-sm"
                                                        disabled={confirmingSuggested || deferringRemaining}
                                                        onClick={() => confirmSuggested(accordionSuggested)}>
                                                    {confirmingSuggested
                                                        ? t(`${NS}.confirming_suggested`)
                                                        : t(`${NS}.confirm_suggested`, {count: accordionSuggested.length})}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    {changes.length > 0 && (
                        // Barra de acción prominente: aparece resaltada cuando hay ≥1 aprobado
                        // (el botón chico al pie costaba encontrarlo — feedback del piloto).
                        <div className={`mp-apply-bar ${approvedCount > 0 ? '-ready' : ''}`}>
                            <span className="mp-apply-text">
                                {needsCurrency
                                    ? t(`${NS}.currency_first`)
                                    : basisAsk
                                        ? t('media_plan.basis.basis_first')
                                    : approvedCount > 0
                                        ? t(`${NS}.apply_ready`, {count: approvedCount})
                                        : t(`${NS}.apply_hint`)}
                            </span>
                            {/* Con la base sin decidir tampoco se aplica: un aprobado que llegó por otra vía
                                (el MCP) se aplicaría con el monto de una tabla a medias. */}
                            <button type="button" className="btn -alt"
                                    disabled={regenerating || approvedCount === 0 || needsCurrency || basisAsk}
                                    onClick={apply}>
                                {applying
                                    ? t(`${NS}.applying`)
                                    : t(`${NS}.apply_approved`, {count: approvedCount})}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // Moneda del plan (UD-2), arriba de las líneas: la moneda con su ORIGEN y «Cambiar» cuando el
    // backend pudo decidirla, o la pregunta cuando no. Es UNA para todo el plan (el plan está en una
    // sola moneda): por eso se elige acá y no fila por fila. Elegir guarda la decisión para las
    // próximas lecturas y rearma la propuesta sin IA. Propuestas viejas (`{}`) → nada.
    function renderPlanCurrency() {
        if (!hasPlanCurrency) return null;
        const options = planCurrency.options || [];
        const renderOptionButtons = (current) => options.filter((code) => code !== current).map((code) => (
            <button key={code} type="button" disabled={regenerating}
                    className={`btn -negative mp-option-btn ${code === planCurrency.suggested ? '-suggested' : ''}`}
                    onClick={() => changePlanCurrency(code)}>
                <span className="mp-option-label">{code}</span>
                {needsCurrency && code === planCurrency.suggested && (
                    <span className="mp-option-badge">{t(`${NS}.plan_currency.suggested`)}</span>
                )}
            </button>
        ));
        // «Otra moneda»: el plan puede estar en una que no es la de ninguna cuenta ni USD.
        const renderOtherCurrency = () => (
            <span className="mp-plan-currency-other">
                <span className="mp-plan-currency-other-label">{t(`${NS}.plan_currency.other`)}</span>
                <CurrencySelect name="plan_currency" selected={null} disabled={regenerating}
                                onChange={(event) => event.target.value && changePlanCurrency(event.target.value)}/>
            </span>
        );

        if (needsCurrency) {
            return (
                <div className="mp-plan-currency -ask" data-testid="mp-plan-currency-ask">
                    <div className="mp-plan-currency-head">
                        <FaIcon iconName="circle-question" className="mp-plan-currency-icon"/>
                        <span className="mp-plan-currency-title">{t(`${NS}.plan_currency.ask_title`)}</span>
                    </div>
                    {/* `div` y no `p`: `_forms.scss` le aplica el estilo de rótulo a todo `p`. */}
                    <div className="mp-plan-currency-text">{t(`${NS}.plan_currency.ask_text`)}</div>
                    <div className="mp-plan-currency-options">
                        {savingCurrency ? <SpinnerButton/> : (
                            <>
                                {renderOptionButtons(null)}
                                {renderOtherCurrency()}
                            </>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div className="mp-plan-currency" data-testid="mp-plan-currency">
                <div className="mp-plan-currency-head">
                    <span className="mp-plan-currency-label">{t(`${NS}.plan_currency.label`)}</span>
                    <strong className="mp-plan-currency-code">{planCurrency.code}</strong>
                    <span className="mp-plan-currency-source">
                        {t(`${NS}.plan_currency.source.${planCurrency.source}`, {defaultValue: ''})}
                    </span>
                    {!currencyPickerOpen && (
                        <button type="button" className="mp-plan-currency-change" disabled={regenerating}
                                onClick={() => setCurrencyPickerOpen(true)}>
                            {t(`${NS}.plan_currency.change`)}
                        </button>
                    )}
                </div>
                {/* La elección guardada gana, pero no en silencio: si hoy la planilla escribe otra
                    moneda, o los montos se parecen al gasto en otra, se dice y se ofrece un clic para
                    volver a la automática (review C-10). */}
                {planCurrency.source === 'USER' && planCurrency.automatic && planCurrency.automatic.code && (
                    <div className="mp-plan-currency-automatic">
                        <FaIcon iconName="triangle-exclamation" className="mp-plan-currency-icon"/>
                        <span>
                            {t(`${NS}.plan_currency.automatic_differs`, {
                                code: planCurrency.automatic.code,
                                source: t(`${NS}.plan_currency.source.${planCurrency.automatic.source}`,
                                    {defaultValue: ''}),
                            })}
                        </span>
                        <button type="button" className="mp-plan-currency-change" disabled={regenerating}
                                onClick={() => changePlanCurrency(null)}>
                            {t(`${NS}.plan_currency.use_automatic`, {code: planCurrency.automatic.code})}
                        </button>
                    </div>
                )}
                {currencyPickerOpen && (
                    <div className="mp-plan-currency-options">
                        {savingCurrency ? <SpinnerButton/> : (
                            <>
                                {renderOptionButtons(planCurrency.code)}
                                {renderOtherCurrency()}
                                {planCurrency.source === 'USER' && (
                                    <button type="button" className="mp-plan-currency-change"
                                            disabled={regenerating} onClick={() => changePlanCurrency(null)}>
                                        {t(`${NS}.plan_currency.auto`)}
                                    </button>
                                )}
                                <button type="button" className="mp-plan-currency-change"
                                        onClick={() => setCurrencyPickerOpen(false)}>
                                    {t(`${NS}.plan_currency.cancel`)}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // «Base de los montos» (tanda «grupos, dinero y DV360»): qué columna de cada tabla es la inversión
    // en plataforma, con de dónde salió, y «Cambiar» si hay otra candidata. Ámbar y bloquea aprobar
    // sólo cuando el código no pudo decidirla en alguna tabla (`ask`). Mismo molde que la moneda.
    function renderPlanBasis() {
        if (!basis.hasPlanBasis) return null;
        const interpolation = {escapeValue: false};
        const multi = basis.tables.length > 1;
        const columnText = (entry) => t('media_plan.basis.decided', {
            column_label: columnLabelOf(entry), column: entry.column, interpolation});
        const tableText = (table) => (multi
            ? t('media_plan.basis.per_table', {n: tableNumber(table), column_label: columnLabelOf(table),
                column: table.column, interpolation})
            : columnText(table));
        const sourceText = (table) => {
            const key = basisSourceKey(table.source);
            return key ? t(`media_plan.basis.${key}`) : '';
        };
        const renderOptions = (table, options) => (
            <div className="mp-plan-currency-options">
                {savingBasis === table.signature ? <SpinnerButton/> : options.map((candidate) => (
                    <button key={candidate.column} type="button" disabled={regenerating}
                            className="btn -negative mp-option-btn"
                            onClick={() => changePlanBasis(table.signature, candidate.column)}>
                        <span className="mp-option-label">{columnText(candidate)}</span>
                    </button>
                ))}
            </div>
        );
        const renderDecided = (table) => {
            const options = basisChangeOptions(table);
            const open = basisPickerFor === table.signature;
            return (
                <div key={table.signature || `t${table.index}`} className="mp-plan-basis-table">
                    <div className="mp-plan-basis-row">
                        {/* Columna y origen en UN texto: el «·» va pegado con espacios duros al
                            origen, así no queda colgado al principio de un renglón a 390. */}
                        <span className="mp-plan-basis-line">
                            <strong className="mp-plan-basis-column">{tableText(table)}</strong>
                            {sourceText(table) && (
                                <>
                                    <span className="mp-plan-basis-source" aria-hidden="true">{' · '}</span>
                                    <span className="mp-plan-basis-source">{sourceText(table)}</span>
                                </>
                            )}
                        </span>
                        {options.length > 0 && !open && (
                            <button type="button" className="mp-plan-currency-change" disabled={regenerating}
                                    onClick={() => setBasisPickerFor(table.signature)}>
                                {t('media_plan.basis.change')}
                            </button>
                        )}
                    </div>
                    {open && (
                        <div className="mp-plan-basis-picker">
                            {renderOptions(table, options)}
                            {savingBasis === null && (
                                <button type="button" className="mp-plan-currency-change"
                                        onClick={() => setBasisPickerFor(null)}>
                                    {t(`${NS}.plan_currency.cancel`)}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            );
        };

        if (basisAsk) {
            return (
                <div className="mp-plan-currency mp-plan-basis -ask" data-testid="mp-plan-basis-ask">
                    <div className="mp-plan-currency-head">
                        <FaIcon iconName="circle-question" className="mp-plan-currency-icon"/>
                        <span className="mp-plan-currency-title">{t('media_plan.basis.ask_title')}</span>
                    </div>
                    {basis.askTables.map((table) => (
                        <div key={table.signature || `t${table.index}`} className="mp-plan-basis-table">
                            {/* `div` y no `p`: `_forms.scss` le aplica el estilo de rótulo a todo `p`. */}
                            <div className="mp-plan-currency-text">
                                {t('media_plan.basis.ask_hint', {n: tableNumber(table)})}
                            </div>
                            {renderOptions(table, (table.candidates || []).filter((c) => c && c.column))}
                        </div>
                    ))}
                    {basis.decided.map(renderDecided)}
                </div>
            );
        }
        return (
            <div className="mp-plan-currency mp-plan-basis" data-testid="mp-plan-basis">
                <span className="mp-plan-currency-label">{t('media_plan.basis.title')}</span>
                {basis.tables.map(renderDecided)}
                <div className="mp-plan-basis-hint">{t('media_plan.basis.decided_hint')}</div>
            </div>
        );
    }

    // «Cómo se reparte el plan en tus líneas»: una fila por grupo del plan (rótulo, monto con su
    // origen y un select con las líneas, «Línea nueva…» y «No seguir desde el plan»), pre-elegida con
    // lo guardado o lo sugerido, el total que recibe cada línea a medida que se elige y «Guardar
    // reparto», que manda todos los grupos con destino. Los grupos nuevos del mes se resaltan.
    function renderGroupMapping() {
        if (!mapping.hasGroupMapping) return null;
        const interpolation = {escapeValue: false};
        const currency = planCurrency.code || '';
        const totals = lineTotals(mapping.groups, mappingChoices, mapping.lines, mapping.tables);
        const canSave = canSaveMapping(mapping.groups, mappingChoices, mapping.lines);
        const setChoice = (key, choice) => setMappingEdits((edits) => ({...edits, [key]: choice}));
        const busy = regenerating || generating;
        // «Como hoy: «X»»: la línea de la sección del grupo (ronda 2). Sin nombres, «Como hoy». Lo distintivo va
        // adelante porque a 390 el select cerrado corta el final.
        const asTodayLabel = (group) => {
            if (isImplicitUntracked(group)) return t('media_plan.group_mapping.implicit_untracked');
            const names = implicitLineNames(group);
            return names.length > 0
                ? t('media_plan.group_mapping.implicit_as_today', {line: names.join(', '), interpolation})
                : t('media_plan.group_mapping.implicit_as_today_unnamed');
        };
        // Una sección con split se reparte por plataforma como dice el plan: sus líneas, sin select.
        const renderSplitLines = (group) => {
            const names = implicitLineNames(group);
            return (
                <span className="mp-group-mapping-line -split" role="cell" data-testid="mp-group-split">
                    <span className="mp-group-mapping-split">{t('media_plan.group_mapping.split_by_platform')}</span>
                    {names.length > 0 && (
                        <span className="mp-group-mapping-split-lines">
                            {t('media_plan.group_mapping.split_lines', {lines: names.join(', '), interpolation})}
                        </span>
                    )}
                </span>
            );
        };
        const renderGroupRow = (group, index) => {
            const choice = mappingChoices[group.key];
            const value = choiceValue(choice);
            const nameTaken = newNameTaken(choice, mapping.lines, group.saved);
            const reasonKey = suggestionReasonKey(group, choice, mapping.lines);
            const source = amountSource(group);
            const labelId = `mp-group-${index}`;
            const label = group.label
                || t('media_plan.group_mapping.group_unnamed', {n: (Number(group.table_index) || 0) + 1});
            return (
                <div key={group.key} role="row" data-testid="mp-group-row"
                     className={`mp-group-mapping-row ${group.is_new ? '-new' : ''}`}>
                    <span className="mp-group-mapping-group" role="cell">
                        <span className="mp-group-mapping-label" id={labelId}>{label}</span>
                        {group.is_new && (
                            <span className="mp-group-mapping-new">{t('media_plan.group_mapping.is_new')}</span>
                        )}
                        {untrackedPlatforms(group).map((p, i) => (
                            <span key={i} className="mp-group-mapping-untracked">
                                {t('media_plan.untracked.row', {platform: p.label || p.platform || '',
                                    amount: formatPlanAmount(p.amount, currency), interpolation})}
                            </span>
                        ))}
                        {/* Una parte del plan que hoy nadie sigue: se dice mientras siga «como hoy». */}
                        {isImplicitUntracked(group) && choice && choice.target === TARGET_SECTION && (
                            <span className="mp-group-mapping-untracked" data-testid="mp-group-untracked-hint">
                                {t('media_plan.group_mapping.implicit_untracked_hint')}
                            </span>
                        )}
                    </span>
                    <span className="mp-group-mapping-amount" role="cell">
                        <span className="mp-group-mapping-amount-value">
                            {formatPlanAmount(group.amount, currency)}
                        </span>
                        {source && (
                            <span className="mp-group-mapping-source">
                                {t(`media_plan.group_mapping.${source.key}`, {...source.values, interpolation})}
                            </span>
                        )}
                    </span>
                    {isSplitGroup(group) ? renderSplitLines(group) : (
                        <span className="mp-group-mapping-line" role="cell">
                            <select className="form-control form-select" aria-labelledby={labelId}
                                    value={value} disabled={busy}
                                    onChange={(event) => setChoice(group.key,
                                        choiceFromValue(event.target.value, mapping.lines, choice))}>
                                {/* «Como hoy» va primero: es lo que pasa si no se elige nada. Ocupa el lugar
                                    de «Elegir…», que sólo queda con el backend de la ronda 1: también deja sin
                                    decidir una sugerencia pre-elegida, y sobre un reparto guardado no se ofrece
                                    (guardar no lo borraría). */}
                                {offersSection(group) && (
                                    <option value="section">{asTodayLabel(group)}</option>
                                )}
                                {(!value || canLeaveUndecided(group, mapping.lines)) && (
                                    <option value="">{t('media_plan.group_mapping.option_choose')}</option>
                                )}
                                {mapping.lines.map((line) => (
                                    <option key={line.bu_id} value={`bu:${line.bu_id}`}>{line.name}</option>
                                ))}
                                <option value="new">{t('media_plan.group_mapping.option_new')}</option>
                                <option value="untracked">{t('media_plan.group_mapping.option_untracked')}</option>
                            </select>
                            {choice && choice.target === TARGET_NEW && (
                                <input type="text" className="form-control mp-group-mapping-name"
                                       value={choice.name || ''} maxLength={NEW_LINE_NAME_MAX_LENGTH} disabled={busy}
                                       placeholder={t('media_plan.group_mapping.option_new_name_placeholder')}
                                       aria-label={t('media_plan.group_mapping.option_new_name_placeholder')}
                                       aria-invalid={nameTaken || undefined}
                                       onChange={(event) => setChoice(group.key,
                                           {target: TARGET_NEW, name: event.target.value})}/>
                            )}
                            {nameTaken && (
                                <span className="mp-group-mapping-reason -warn" data-testid="mp-group-name-taken">
                                    {t('media_plan.group_mapping.name_taken')}
                                </span>
                            )}
                            {reasonKey && (
                                <span className="mp-group-mapping-reason">
                                    {t(`media_plan.group_mapping.${reasonKey}`)}
                                </span>
                            )}
                        </span>
                    )}
                </div>
            );
        };
        return (
            <section className={`mp-group-mapping ${mapping.pending ? '-pending' : ''}`}
                     aria-labelledby="mp-group-mapping-title" data-testid="mp-group-mapping">
                <div className="mp-list-heading">
                    <h3 id="mp-group-mapping-title" className="mp-list-title">{t('media_plan.group_mapping.title')}</h3>
                    <div className="mp-list-subtitle">{t('media_plan.group_mapping.hint')}</div>
                    {mapping.implicitHint && (
                        <div className="mp-list-subtitle" data-testid="mp-group-implicit-hint">
                            {t('media_plan.group_mapping.implicit_hint')}
                        </div>
                    )}
                </div>
                {/* Sugerencias que cambian la propuesta de hoy (nada guardado todavía): informativo, en
                    azul. No bloquea: sin guardar, la propuesta sigue siendo la de hoy. */}
                {mapping.suggestedNotice && (
                    <div className="mp-group-mapping-suggested" data-testid="mp-group-suggested-notice">
                        <FaIcon iconName="info-circle" className="icon"/>
                        <span>{t('media_plan.group_mapping.suggested_notice')}</span>
                    </div>
                )}
                {mapping.pending && (
                    <div className="mp-group-mapping-pending">
                        <FaIcon iconName="triangle-exclamation" className="icon"/>
                        <span>{t('media_plan.group_mapping.pending_notice')}</span>
                    </div>
                )}
                <div className="mp-group-mapping-table" role="table"
                     aria-label={t('media_plan.group_mapping.title')}>
                    <div className="mp-group-mapping-row -head" role="row">
                        <span role="columnheader">{t('media_plan.group_mapping.col_group')}</span>
                        <span role="columnheader">{t('media_plan.group_mapping.col_amount')}</span>
                        <span role="columnheader">{t('media_plan.group_mapping.col_line')}</span>
                    </div>
                    {mapping.groups.map(renderGroupRow)}
                </div>
                <div className="mp-group-mapping-foot">
                    <div className="mp-group-mapping-totals" data-testid="mp-group-totals">
                        {totals.destinations.map((destination) => (
                            <div key={destination.key} className="mp-group-mapping-total">
                                {t('media_plan.group_mapping.line_total', {line: destination.name,
                                    amount: formatBudgetWithCurrency(destination.amount, currency), interpolation})}
                            </div>
                        ))}
                        {totals.untracked > 0 && (
                            <div className="mp-group-mapping-total -untracked">
                                {t('media_plan.group_mapping.untracked_amount', {
                                    amount: formatBudgetWithCurrency(totals.untracked, currency), interpolation})}
                            </div>
                        )}
                    </div>
                    {/* Si todos los grupos se reparten por plataforma, no hay nada que guardar. Con sugerencias
                        sin guardar, «Seguir como hoy» las descarta (ronda 2b): mientras tanto el backend no
                        propone la línea de hoy de esas secciones. */}
                    {mapping.hasChoosable && (
                        <div className="mp-group-mapping-actions">
                            {mapping.suggestedNotice && (
                                <button type="button" className="btn -alt -negative mp-btn-sm" disabled={busy}
                                        data-testid="mp-group-dismiss" onClick={() => saveGroupMapping(true)}>
                                    {savingMapping === 'dismiss'
                                        ? t('media_plan.group_mapping.saving')
                                        : t('media_plan.group_mapping.suggested_dismiss')}
                                </button>
                            )}
                            <button type="button" className="btn -alt mp-btn-sm" disabled={!canSave || busy}
                                    onClick={() => saveGroupMapping()}>
                                {savingMapping === 'save'
                                    ? t('media_plan.group_mapping.saving')
                                    : t('media_plan.group_mapping.save')}
                            </button>
                        </div>
                    )}
                </div>
            </section>
        );
    }

    // Filas del plan que no se siguen (plataforma sin canal, o la que el usuario eligió no seguir) y
    // la oferta de sumar la cuenta DV360, dentro de la card. Renglones NEUTROS: no son dudas, no
    // bloquean la línea y no cuentan en la pill de pendientes. «Sumar la cuenta a la línea» aprueba
    // el item que la trae, igual que su «Aprobar».
    // `blockReason`: por qué no se puede aprobar (moneda o base sin elegir), dicho junto a la oferta,
    // porque el motivo del pie no se ve desde acá.
    function renderUntracked(item, approveDisabled, blockReason = null) {
        const payload = item.payload || {};
        // El item de la oferta puede no traer moneda: la del plan.
        const currency = payload.currency || planCurrency.code || '';
        const rows = untrackedRowsOf(item);
        const offer = dv360OfferOf(item, proposal && proposal.group_mapping);
        if (rows.length === 0 && !offer) return null;
        // Con la oferta, cuánto le queda a la línea si NO se suma la cuenta (lo trae su item de presupuesto).
        const withoutOffer = offer ? amountWithoutOfferFor(item, items) : null;
        const interpolation = {escapeValue: false};
        const canOffer = item.review_status === 'PENDING_REVIEW' && APPLIABLE.has(item.action_type);
        return (
            <div className="mp-untracked" data-testid="mp-untracked">
                {rows.map((row, i) => (
                    <div key={`u${i}`} className="mp-untracked-row" data-testid="mp-untracked-row">
                        <FaIcon iconName="ban" className="mp-untracked-icon"/>
                        <div className="mp-untracked-body">
                            <span className="mp-untracked-text">
                                {t('media_plan.untracked.row', {platform: row.platform,
                                    amount: formatPlanAmount(row.amount, currency), interpolation})}
                            </span>
                            {row.reasonKey && (
                                <span className="mp-untracked-reason">{t(`media_plan.untracked.${row.reasonKey}`)}</span>
                            )}
                        </div>
                    </div>
                ))}
                {offer && (
                    <div className="mp-untracked-row -offer" data-testid="mp-dv360-offer">
                        <FaIcon iconName="link" className="mp-untracked-icon"/>
                        <div className="mp-untracked-body">
                            <span className="mp-untracked-text">
                                {t('media_plan.untracked.dv360_offer', {platform: offer.platform,
                                    amount: formatPlanAmount(offer.amount, currency),
                                    account: offer.account, interpolation})}
                            </span>
                            {withoutOffer !== null && (
                                <span className="mp-untracked-reason" data-testid="mp-dv360-offer-without">
                                    {t('media_plan.untracked.dv360_offer_without', {
                                        amount: formatPlanAmount(withoutOffer, currency), interpolation})}
                                </span>
                            )}
                            {canOffer && approveDisabled && blockReason && (
                                <span className="mp-untracked-reason" data-testid="mp-dv360-offer-blocked">
                                    {blockReason}
                                </span>
                            )}
                        </div>
                        {canOffer && (
                            <button type="button" className="btn -alt -negative mp-btn-sm"
                                    disabled={approveDisabled} onClick={() => review(item, 'approve')}>
                                {t('media_plan.untracked.dv360_offer_action')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Decisiones estructurales: conservan el texto y TODAS las opciones del productor. Sólo el
    // contrato budget_for:: admite entrada numérica; una decisión desconocida no inventa un input.
    function renderPlanDecisions(kind, decisions) {
        if (decisions.length === 0) return null;
        const copy = PLAN_DECISION_COPY[kind];
        const titleId = `mp-plan-${kind}-title`;
        return (
            <section key={kind} className={`mp-plan-${kind}`} aria-labelledby={titleId}>
                <div className="mp-list-heading">
                    <h3 id={titleId} className="mp-list-title">{t(`${NS}.${copy.title}`)}</h3>
                    <p className="mp-list-subtitle">{t(`${NS}.${copy.hint}`)}</p>
                </div>
                <ul className="mp-question-list">
                    {decisions.map((q) => {
                        const question = q.question || {};
                        const textId = `mp-decision-${q.id}`;
                        const amount = questionBudgets[q.id];
                        const validAmount = Number.isFinite(Number(amount)) && Number(amount) > 0;
                        return (
                            <li key={q.id} className="mp-question">
                                <span id={textId} className="mp-question-anchor">{question.question_text}</span>
                                <div className="mp-question-picks">
                                    {kind === 'budget' ? (
                                        <>
                                            <BudgetValueInput budgetAmount={amount || ''}
                                                budgetCurrency={planCurrency.code || ''} hideCurrency
                                                numberLocale="es-AR" readOnly={regenerating}
                                                amountInputId={`mp-budget-answer-${q.id}`}
                                                amountAriaLabelledBy={textId}
                                                onAmountChange={(value) => setQuestionBudgets((current) =>
                                                    ({...current, [q.id]: value}))}/>
                                            <button type="button" className="btn -alt mp-btn-sm"
                                                    disabled={regenerating || !validAmount}
                                                    onClick={() => answer(q, null, String(Number(amount)))}>
                                                {t(`${NS}.decision.confirm_budget`)}
                                            </button>
                                        </>
                                    ) : (question.options || []).map((option) => {
                                        // «No seguir…» (con el flag) llega con el texto del backend en español:
                                        // se dice con el copy del idioma de la pantalla.
                                        const labelKey = untrackedOptionLabelKey(q, option);
                                        return renderQuestionOption(q, labelKey ? {...option, label: t(labelKey)} : option);
                                    })}
                                    {kind === 'account' && (
                                        <a className="mp-question-escape" href={`/client/${clientId}/conections`}>
                                            {t(`${NS}.account.none_of_these`)}
                                        </a>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </section>
        );
    }

    // Un botón de opción de una duda (la sugerida lleva borde naranja + chip; clic = responder).
    function renderQuestionOption(q, option) {
        return (
            <button key={option.key} type="button"
                    className={`btn -negative mp-option-btn ${option.suggested || option.recommended ? '-suggested' : ''}`}
                    disabled={regenerating}
                    onClick={() => answer(q, option.key)}
                    title={option.label}>
                <span className="mp-option-label">{option.label}</span>
                {option.suggested && (
                    <span className="mp-option-badge">
                        {option.match_count > 0
                            ? t(`${NS}.account.suggested_n`, {count: option.match_count})
                            : t(`${NS}.account.suggested`)}
                    </span>
                )}
                {option.linked === false && (
                    <span className="mp-option-note">{t(`${NS}.account.will_link`)}</span>
                )}
            </button>
        );
    }

    // Una duda compacta (progressive disclosure): ancla = la campaña del plan (`subject`, la cuenta ya
    // está en el encabezado del grupo); la sugerida (o la 1ra) es la acción primaria y el resto de los
    // candidatos se despliega bajo demanda con «+N opciones». Así 18 dudas dejan de ser un muro.
    function renderQuestion(q, index) {
        const question = q.question || {};
        // "Asignar más adelante" (2026-07-22) viaja como opción del backend (última, contrato) pero
        // se renderiza como ACCIÓN secundaria, nunca entre las candidatas.
        const allOptions = question.options || [];
        const deferOption = allOptions.find((o) => o.key === DEFER_KEY) || null;
        const options = allOptions.filter((o) => o.key !== DEFER_KEY);
        const isDeferred = !!question.deferred;
        const primary = options.find((o) => o.suggested || o.recommended) || options[0];
        // ¿La opción primaria es una recomendación (match con confianza) o solo la mejor candidata
        // del ranking? Sin sugerencia, la opción NO es una recomendación → se avisa para que el
        // usuario la revise en vez de confirmar a ciegas la primera que aparece.
        const hasSuggestion = !!(primary && (primary.suggested || primary.recommended));
        const rest = options.filter((o) => o !== primary);
        // Duda SIN candidatas (sólo "Asignar más adelante"): la campaña del plan todavía no existe en
        // la cuenta → no hay nada que identificar, sólo posponer. No es un "sin coincidencia clara".
        const onlyDefer = !primary && !!deferOption;
        const expanded = !!expandedQuestions[q.id];
        const anchor = questionAnchor(question);
        return (
            <li key={q.id} className={`mp-question ${isDeferred ? '-deferred' : ''}`}
                style={{animationDelay: `${Math.min(index, 12) * 30}ms`}}>
                <span className="mp-question-anchor" title={question.question_text}>
                    {anchor ? `«${anchor}»` : question.question_text}
                    {isDeferred && (
                        <span className="mp-question-deferred-chip">{t(`${NS}.deferred_badge`)}</span>
                    )}
                </span>
                <div className="mp-question-picks">
                    {onlyDefer && !isDeferred ? (
                        <span className="mp-question-notyet">{t(`${NS}.campaign_not_in_account_yet`)}</span>
                    ) : (!hasSuggestion && !isDeferred && (
                        <span className="mp-question-nomatch">{t(`${NS}.no_clear_match`)}</span>
                    ))}
                    {primary && renderQuestionOption(q, primary)}
                    {expanded && rest.map((o) => renderQuestionOption(q, o))}
                    {rest.length > 0 && !expanded && (
                        <button type="button" className="mp-question-more"
                                onClick={() => setExpandedQuestions((s) => ({...s, [q.id]: true}))}>
                            {t(`${NS}.question_more`, {count: rest.length})}
                            <FaIcon iconName="chevron-down" className="mp-question-more-caret"/>
                        </button>
                    )}
                    {deferOption && !isDeferred && (
                        <button type="button" className="mp-question-defer"
                                disabled={regenerating}
                                title={t(`${NS}.defer_action_hint`)}
                                onClick={() => answer(q, DEFER_KEY)}>
                            {t(`${NS}.defer_action`)}
                        </button>
                    )}
                </div>
            </li>
        );
    }

    // Modo lista (UX actual): la card completa. El cuerpo se comparte con el acordeón (UX 2.1) vía
    // `renderItemInner`, así ambas vistas usan exactamente la misma maquinaria de estado y sub-renders.
    function renderItem(item, index) {
        return (
            <li key={item.id}
                className={`mp-change mp-item -${item.review_status.toLowerCase()}`}
                style={{animationDelay: `${Math.min(index, 12) * 45}ms`}}>
                {renderItemInner(item)}
            </li>
        );
    }

    // Cuerpo de un item, SIN el `<li>` contenedor: compartido entre la lista (UX actual) y el acordeón
    // (UX 2.1). `opts.inAccordion` omite el `mp-change-head` (el header del acordeón ya muestra título
    // + estado, así no se duplica). Todo lo demás —paneles, inputs, footer, gates— es idéntico.
    function renderItemInner(item, opts = {}) {
        const payload = item.payload || {};
        const before = item.current_state || {};
        const isPending = item.review_status === 'PENDING_REVIEW';
        const {monthFirstDay, monthLastDay} = monthBounds();   // para el configurador de tramos
        const evidenceUrl = cellUrl(connection.external_id, item.evidence);
        const budgetAmount = editedBudgets[item.id] !== undefined
            ? editedBudgets[item.id] : payload.allocated_budget;
        const matched = (payload.accounts || [])
            .reduce((n, a) => n + ((a.campaigns || []).length), 0)
            + (payload.additions || []).length;   // UPDATE_BU_CAMPAIGNS: nuevas ya identificadas
        const unmatched = (payload.unmatched_campaigns || []).length;
        const cardAccount = (payload.accounts || [])[0] || {};
        const cardGroupKey = questionGroupKey(cardAccount.platform, cardAccount.ad_account_name);
        // Sin dudas de alias para esta cuenta no hay a dónde saltar: el backend NO pregunta por una
        // campaña del plan sin ningún candidato parecido en el catálogo (todavía no existe en la
        // cuenta). En ese caso el CTA "Identificar N campañas" sería un click muerto → se muestra
        // la nota de "aún no está en la cuenta" en su lugar.
        const groupHasQuestions = questionGroups.some((g) => g.key === cardGroupKey);
        const campaignEntries = (payload.accounts || []).flatMap((a) => a.campaigns || []);
        const hasCampaignGoals = campaignEntries.some((c) => (c.goals || []).length > 0);
        // Fidelidad al plan por-campaña de ESTA card. El plan detalla campañas con presupuesto/objetivo
        // propios; según cuántas se identificaron la línea refleja el plan (todas), lo refleja parcial,
        // o cae al fallback de CUENTA ENTERA (0 identificadas → mide toda la cuenta, incl. campañas
        // ajenas al plan). El re-encuadre hace explícito ese gap en vez de esconderlo como "opcional".
        const planTotal = matched + unmatched;
        const isWholeAccountFallback = planTotal > 0 && matched === 0;
        // Candidatas de vinculación: líneas existentes que parecen ser esta línea del plan. Solo en
        // CREATE pendiente (una vez decidido el destino, la card renace por regeneración). Mientras
        // haya candidatas sin decidir, la aprobación queda bloqueada (destino antes que aprobación).
        const linkCandidates = item.action_type === 'CREATE_BU' && isPending
            ? (payload.link_candidates || []) : [];
        const hasLinkDecision = linkCandidates.length > 0;
        // Gate de resolución completa (2026-07-22): la línea de CAMPAÑAS del plan no se aprueba
        // mientras queden campañas sin resolver (ni identificadas ni pospuestas). Espeja el 400
        // del backend. Solo cards con `campaigns_line` (la global/BU3/modo clásico no se gatean).
        const pendingCount = isGatedItem(item) ? pendingUnmatched(payload).length : 0;
        const deferredCount = deferredUnmatched(payload).length;
        // Lista unificada de campañas del plan (2026-07-25): las POSPUESTAS se muestran junto a las
        // IDENTIFICADAS en el panel, con su monto del plan (que se sumará a la línea cuando existan) —
        // así el estado de las N campañas del plan se ve de un solo vistazo, no partido en dos zonas.
        const deferredEntries = deferredUnmatched(payload);
        const deferredSum = deferredEntries.reduce((s, u) => s + (Number(u.monthly_amount) || 0), 0);
        // Des-posponer (2026-07-28): la duda de alias de una campaña pospuesta se re-emite `deferred`
        // con sus opciones intactas; elegir una campaña real limpia el defer en el backend (idempotente).
        // Matchea por nombre del plan (subject) + cuenta, para ofrecer "Elegir campaña" junto a la
        // pospuesta. El backend guarda el `subject` de la duda con `.strip()` pero el `name_raw` del
        // remanente SIN strip → comparar crudo perdía el match con celdas del Sheet con espacios al
        // borde (y el botón desaparecía sin error). Se compara normalizado, y se exige cuenta no vacía
        // para no matchear por `undefined === undefined` si la card no trae cuentas.
        const sameName = (a, b) => String(a || '').trim() === String(b || '').trim();
        const deferredQuestionFor = (u) => (cardAccount.ad_account_name ? items.find((it) =>
            it.action_type === 'NEEDS_DECISION' && it.review_status === 'PENDING_REVIEW'
            && (it.question || {}).deferred
            && sameName((it.question || {}).subject, u.name_raw)
            && sameName(((it.question || {}).group || {}).account_name, cardAccount.ad_account_name)) : null);
        const identificationPending = isPending && pendingCount > 0;
        // El banner (taller de identificación) aparece SÓLO si hay PENDIENTES sin resolver o es el
        // fallback de cuenta entera. Si sólo quedan POSPUESTAS, la lista unificada ya las muestra con su
        // estado → sin banner (no duplicar el estado en dos zonas — rediseño 2026-07-25).
        const showPlanFidelity = isPending && (isWholeAccountFallback || identificationPending);
        // Lo que queda por accionar en el acordeón (el CTA no cuenta las pospuestas, que ya están
        // resueltas para el gate); en el modo clásico (sin flags) es el remanente entero.
        const unresolvedCount = isGatedItem(item) ? pendingCount : unmatched;
        // Progreso en plata (pedido fede 2026-07-22): cuánto del presupuesto del plan ya está
        // identificado. Suma de las campañas identificadas vs esa suma + el remanente.
        const identifiedSum = campaignEntries.reduce((s, c) => s + (Number(c.monthly_amount) || 0), 0)
            + (payload.additions || []).reduce((s, a) => s + (Number(a.monthly_amount) || 0), 0);
        const planSum = identifiedSum + (payload.unmatched_campaigns || [])
            .reduce((s, u) => s + (Number(u.monthly_amount) || 0), 0);
        // Identificación INLINE de ESTA card (2026-07-24): sus dudas (las del grupo de su cuenta)
        // se resuelven acá mismo, no en el acordeón al pie. Default ABIERTA cuando hay pendientes
        // (con el gate, resolverlas es obligatorio para aprobar) — el CTA solo pliega/despliega.
        const cardGroup = questionGroups.find((g) => g.key === cardGroupKey);
        const cardActiveQs = cardGroup
            ? cardGroup.questions.filter((q) => !(q.question || {}).deferred) : [];
        // Las POSPUESTAS ya no se muestran como dudas en el banner: viven en la lista unificada de
        // campañas (via `deferredEntries` del payload). El taller inline es sólo para PENDIENTES.
        const renderInline = isPending && isGatedItem(item) && cardActiveQs.length > 0;
        const inlineOpen = !collapsedCardIds[item.id];
        // Bulk por-card (solo con ≥2: con una sola, sus botones propios alcanzan).
        const cardSuggested = cardActiveQs
            .map((q) => ({q, option: suggestedAliasOption(q.question)}))
            .filter((x) => x.option)
            .map((x) => ({item_id: x.q.id, answer_key: x.option.key}));
        const cardDeferrable = cardActiveQs
            .filter((q) => ((q.question || {}).options || []).some((o) => o.key === DEFER_KEY))
            .map((q) => ({item_id: q.id, answer_key: DEFER_KEY}));
        // Rol de la card dentro del par de plataforma (2026-07-24): solo cuando el canal tiene sus
        // DOS líneas (global + campañas) → aclara qué mide cada una y evita tratarlas como sueltas.
        const isGroupedPair = platformCardCounts[cardPlatform(item)] >= 2;
        const roleKey = !isGroupedPair ? null
            : payload.campaigns_line ? 'role_campaigns'
                : (item.action_type === 'CREATE_BU' && !payload.advisory ? 'role_global' : null);
        // El "taller" de identificación se pega al banner de estado (un solo panel) — solo cuando de
        // verdad va a mostrar algo (dudas pendientes desplegadas), para no dejar una caja vacía adjunta.
        const inlineHasContent = renderInline && inlineOpen && cardActiveQs.length > 0;
        // Lo que impide aprobar esta fila: lo usan el «Aprobar» del pie y el «Sumar la cuenta a la
        // línea» de la oferta de DV360, que aprueba el mismo item.
        // `regenerating`: mientras otra acción rearma la propuesta (o corre «Volver a leer»), esta fila ya
        // es vieja y lo que se apruebe se pierde con la respuesta.
        const approveDisabled = busyItemId === item.id || hasLinkDecision || identificationPending
            || nameIsBlank(item) || needsCurrency || basisAsk || regenerating;

        return (
            <>
                {!opts.inAccordion && (
                <div className="mp-change-head">
                    <span className="mp-change-title">
                        {t(`${NS}.action.${item.action_type}`, {defaultValue: item.action_type})}
                        {payload.name ? `: ${payload.name}` : ''}
                    </span>
                    {/* El chip de scope se oculta en la card gateada con 0 identificadas:
                        "Cuenta completa" contradice al "En progreso" (la línea aspira a ser por
                        campaña). Cuando el gate se abre, vuelve y refleja el scope real. */}
                    {planTotal > 0 && !(identificationPending && matched === 0) && (
                        <span className="info-icon-container mp-scope-chip-wrap">
                            <span className={`mp-scope-chip ${isWholeAccountFallback ? '-account' : '-campaign'}`}>
                                {t(`${NS}.${isWholeAccountFallback ? 'scope_whole_account' : 'scope_per_campaign'}`)}
                            </span>
                            <span className="warning-tooltip -below -rich">
                                <span className="warning-tooltip-body">
                                    <span className="warning-tooltip-text">
                                        {t(`${NS}.scope_tooltip.${isWholeAccountFallback ? 'whole_account' : 'per_campaign'}`)}
                                    </span>
                                </span>
                            </span>
                        </span>
                    )}
                    {identificationPending && (
                        // "En progreso" (2026-07-22): la línea del plan existe desde el arranque
                        // pero no se puede aprobar hasta resolver todas sus campañas.
                        <span className="mp-progress-chip">{t(`${NS}.in_progress_badge`)}</span>
                    )}
                    {/* Confianza: solo se muestra cuando NO es alta (2026-07-24). "Confianza alta"
                        en TODAS las cards era ruido — el chip sirve para ADVERTIR de una lectura
                        dudosa (media/baja), no para confirmar lo esperado. */}
                    {item.confidence && item.confidence !== 'HIGH' && (
                        <span className={`mp-confidence ${CONFIDENCE_CLASS[item.confidence] || ''}`}>
                            {t(`${NS}.confidence.${item.confidence.toLowerCase()}`, {defaultValue: item.confidence})}
                        </span>
                    )}
                    {!isPending && (
                        <span className={`mp-change-status -${item.review_status.toLowerCase()}`}>
                            {t(`${NS}.review.${item.review_status.toLowerCase()}`, {defaultValue: item.review_status})}
                        </span>
                    )}
                </div>
                )}

                {/* Rol de la línea dentro del par de plataforma (global mide cuenta entera + pacing/
                    objetivo; campañas lleva el detalle por campaña) — cada una remite a la otra. */}
                {roleKey && (
                    <p className="mp-change-role">{t(`${NS}.${roleKey}`)}</p>
                )}

                {/* Bloque de decisión de destino: cuando esta línea del plan tiene candidatas de
                    vinculación (líneas ya creadas a mano que parecen ser esta), el usuario elige
                    vincular o crear nueva ANTES de aprobar. Responder regenera la propuesta. */}
                {hasLinkDecision && renderLinkDecision(item, linkCandidates)}

                {/* P1 (2026-07-30): destino ELEGIDO visible + "Cambiar". `link_decision` solo viaja
                    cuando la resolución vino de una decisión del usuario (pin), nunca del name-match
                    histórico; propuestas viejas no lo traen → la franja degrada en silencio. El
                    "Cambiar" abre una confirmación inline (deshace el pin + regenera; NO revierte
                    lo ya aplicado). En REJECTED/STALE no se muestra (la card ya no gobierna nada). */}
                {payload.link_decision && payload.line_key
                    && !['REJECTED', 'STALE'].includes(item.review_status) && (
                    <div className="mp-link-note">
                        {isConfirmingUndo(payload.line_key, 'row')
                            ? renderUndoConfirmRow(payload.line_key)
                            : (
                                <div className="mp-link-note-row">
                                    <FaIcon iconName={payload.link_decision.kind === 'LINKED' ? 'link' : 'plus'}
                                            className="mp-link-note-icon"/>
                                    <span className="mp-link-note-text">
                                        {payload.link_decision.kind === 'LINKED'
                                            ? t(`${NS}.link_note.linked`, {
                                                name: payload.link_decision.bu_name || payload.name,
                                                interpolation: {escapeValue: false},
                                            })
                                            : t(`${NS}.link_note.new`)}
                                    </span>
                                    <button type="button" className="mp-link-note-change"
                                            onClick={() => setUndoTarget({lineKey: payload.line_key, where: 'row'})}>
                                        {t(`${NS}.link_note.change`)}
                                    </button>
                                </div>
                            )}
                    </div>
                )}

                {/* Nombre de la línea (card zMQIrxFe): el plan propone uno y el usuario lo cambia ANTES
                    de aprobar. No es cosmético — cuando la cuenta publicitaria mezcla las campañas de
                    la agencia con las de los proveedores del cliente (miles), el nombre es lo único
                    con lo que después se filtra lo propio en el tablero. Sólo en una línea que se va a
                    CREAR: con una decisión de destino sin resolver todavía no se sabe si se crea algo,
                    y si el destino fue "vincular" la línea ya existe y su nombre no lo gobierna el plan. */}
                {item.action_type === 'CREATE_BU' && isPending && !hasLinkDecision
                    && (payload.link_decision || {}).kind !== 'LINKED' && renderNameEditor(item)}

                {/* "Qué va a medir esta línea" — UNA CAJA POR CUENTA (feedback fede 2026-07-30: el
                    alcance y la modalidad de selección estaban en micro-texto gris y "el usuario no
                    lee los textos"). Mismo patrón que el preview por cuenta del wizard de armado, que
                    Pablo validó: cuenta + modalidad + qué campañas + evento de conversión. Aplica a
                    TODA card de CREATE, incluida la BU3 (`advisory`), que antes no mostraba nada.
                    Todo derivado del payload; los campos nuevos degradan en silencio si no están. */}
                {renderLineScope(item, {identificationPending, matched, hasCampaignGoals})}

                {/* Filas del plan que no se siguen y la oferta de DV360 (tanda «grupos, dinero y
                    DV360»): renglones neutros, no dudas — no frenan la línea ni cuentan como
                    pendientes. Sin `untracked_rows` / `dv360_offer` en el payload, nada. */}
                {renderUntracked(item, approveDisabled,
                    needsCurrency ? t(`${NS}.currency_first`) : basisAsk ? t('media_plan.basis.basis_first') : null)}

                <div className="mp-change-meta">
                    {item.action_type === 'UPDATE_BU_BUDGET' && (
                        <span>{t(`${NS}.budget_diff`, {
                            before: before.allocated_budget, currency: payload.currency || '',
                            interpolation: {escapeValue: false},
                        })}</span>
                    )}
                    {/* "Cuentas: X, Y" y "N campañas con presupuesto" ya no van acá: el bloque
                        "Qué va a medir esta línea" de arriba los muestra por cuenta y con su
                        modalidad (2026-07-30). En el CREATE quedan solo el link a la planilla y,
                        si aplica, el diff de presupuesto. */}
                    {item.action_type !== 'CREATE_BU' && matched > 0 && (
                        <span>{t(`${NS}.campaigns_configured`, {count: matched})}</span>
                    )}
                    {evidenceUrl && (
                        <CellPeek agencyClientId={clientId} snapshotId={snapshot && snapshot.id}
                                  cell={peekCell(item.evidence)}>
                            <a href={evidenceUrl} target="_blank" rel="noopener noreferrer" className="mp-evidence-link">
                                <FaIcon iconName="up-right-from-square"/>
                                {t(`${NS}.evidence_link`, {
                                    cell: item.evidence.cell || item.evidence.tab_title,
                                    interpolation: {escapeValue: false},
                                })}
                            </a>
                        </CellPeek>
                    )}
                </div>

                {/* Fidelidad al plan por-campaña: hace explícito que el plan es POR CAMPAÑA y qué falta
                    para reflejarlo. Fallback de cuenta entera (0 identificadas) → aviso de que mide toda
                    la cuenta + CTA a identificar; parcial → progreso + CTA. El CTA salta al acordeón. */}
                {showPlanFidelity && (
                    // Card GATEADA → UN SOLO bloque azul: estado ARRIBA + identificación inline
                    // ANIDADA abajo, en el mismo contenedor (imposible que se vean como dos cajas —
                    // fix 2026-07-24). El ámbar queda solo para el fallback de cuenta entera no gateado.
                    <div className={`mp-plan-fidelity ${
                        !identificationPending && isWholeAccountFallback ? '-fallback' : '-partial'}`}>
                      <div className="mp-plan-fidelity-head">
                        <FaIcon iconName={!identificationPending && isWholeAccountFallback
                            ? 'triangle-exclamation' : 'info-circle'} className="icon"/>
                        <div className="mp-plan-fidelity-body">
                            <span className="mp-plan-fidelity-text">
                                {/* `count` activa los plurales _one/_other (bug "las 1 restantes",
                                    2026-07-23). El llamado a identificar cuenta solo PENDIENTES
                                    (las pospuestas ya están resueltas para el gate). */}
                                {identificationPending
                                    ? (matched > 0
                                        ? t(`${NS}.plan_fidelity_gated`,
                                            {identified: matched, total: planTotal,
                                             remaining: pendingCount, count: pendingCount})
                                        : t(`${NS}.plan_fidelity_gated_none`,
                                            {total: planTotal, count: planTotal}))
                                    : isWholeAccountFallback
                                        ? t(`${NS}.plan_fidelity_fallback`,
                                            {total: planTotal, count: planTotal})
                                        : groupHasQuestions && unresolvedCount > 0
                                            ? t(`${NS}.plan_fidelity_partial`,
                                                {identified: matched, total: planTotal,
                                                 remaining: unresolvedCount, count: unresolvedCount})
                                            : t(`${NS}.plan_fidelity_partial_count`,
                                                {identified: matched, total: planTotal})}
                            </span>
                            {/* Progreso en plata (2026-07-22): con 0 identificadas la "suma $0"
                                no informa — se muestra el presupuesto del plan (2026-07-23). */}
                            {planSum > 0 && (
                                <span className="mp-plan-fidelity-progress">
                                    {identifiedSum > 0
                                        ? t(`${NS}.campaigns_progress`, {
                                            sumA: formatBudgetWithCurrency(identifiedSum, payload.currency),
                                            sumB: formatBudgetWithCurrency(planSum, payload.currency),
                                            interpolation: {escapeValue: false},
                                        })
                                        : t(`${NS}.plan_budget_total`, {
                                            sum: formatBudgetWithCurrency(planSum, payload.currency),
                                            interpolation: {escapeValue: false},
                                        })}
                                    {deferredCount > 0
                                        && ` · ${t(`${NS}.deferred_note`, {count: deferredCount})}`}
                                </span>
                            )}
                            {unresolvedCount > 0 && (groupHasQuestions ? (
                                // Pliega/despliega la identificación INLINE de abajo (default
                                // abierta) — ya no salta a un acordeón al pie (2026-07-24).
                                <button type="button" className="mp-plan-fidelity-cta"
                                        onClick={() => setCollapsedCardIds(
                                            (s) => ({...s, [item.id]: inlineOpen}))}>
                                    {t(`${NS}.identify_campaigns_cta`, {count: unresolvedCount})}
                                    <FaIcon iconName={inlineOpen ? 'chevron-up' : 'chevron-down'}
                                            className="mp-plan-fidelity-caret"/>
                                </button>
                            ) : (
                                <span className="mp-plan-fidelity-pending">
                                    {t(`${NS}.identify_campaigns_pending_sync`, {count: unresolvedCount})}
                                </span>
                            ))}
                        </div>
                      </div>
                      {/* Identificación INLINE (2026-07-24): DENTRO del mismo bloque azul (no una
                          caja aparte) → estado arriba, dudas abajo, separadas por un divisor fino. */}
                      {inlineHasContent && (
                        <div className="mp-plan-fidelity-questions">
                        {inlineOpen && cardActiveQs.length > 0 && (
                            <>
                                <ul className="mp-question-list">
                                    {cardActiveQs.map((q, i) => renderQuestion(q, i))}
                                </ul>
                                {(cardSuggested.length >= 2 || cardDeferrable.length >= 2) && (
                                    <div className="mp-questions-footer">
                                        {cardDeferrable.length >= 2 && (
                                            <span className="mp-defer-bulk">
                                                <button type="button" className="btn -alt -negative mp-btn-sm"
                                                        disabled={deferringRemaining || confirmingSuggested}
                                                        onClick={() => deferRemaining(cardDeferrable)}>
                                                    {deferringRemaining
                                                        ? t(`${NS}.deferring_remaining`)
                                                        : t(`${NS}.defer_remaining`, {count: cardDeferrable.length})}
                                                </button>
                                                {/* Tooltip canónico de Dashbo: explica qué significa
                                                    posponer (la campaña aún no existe en la cuenta). */}
                                                <span className="info-icon-container mp-defer-info">
                                                    <FaIcon iconName="info-circle"/>
                                                    <span className="warning-tooltip -below -rich">
                                                        <span className="warning-tooltip-body">
                                                            <span className="warning-tooltip-text">
                                                                {t(`${NS}.defer_tooltip`)}
                                                            </span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </span>
                                        )}
                                        {cardSuggested.length >= 2 && (
                                            <button type="button" className="btn -alt mp-btn-sm"
                                                    disabled={confirmingSuggested || deferringRemaining}
                                                    onClick={() => confirmSuggested(cardSuggested)}>
                                                {confirmingSuggested
                                                    ? t(`${NS}.confirming_suggested`)
                                                    : t(`${NS}.confirm_suggested`, {count: cardSuggested.length})}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                        </div>
                      )}
                    </div>
                )}

                {/* Nota de la línea (BU3 objetivo global): aclara que esta línea es la lente del
                    resultado global y que el pacing/alertas se miran por las líneas por plataforma.
                    Texto del backend → se muestra crudo; sin él degrada sin romper. */}
                {payload.advisory && (
                    <div className="mp-change-advisory">
                        <FaIcon iconName="info-circle" className="icon"/>
                        <span>{payload.advisory}</span>
                    </div>
                )}

                {/* Presupuesto mensual: EL dato de la línea (una línea de control es ante todo una
                    línea de presupuesto) → vive en el cuerpo, ANTES de los objetivos y con la misma
                    jerarquía de label — no como campo menor del footer (feedback fede 2026-07-18).
                    Sigue editable inline antes de aprobar. */}
                {/* Línea de CAMPAÑAS con matches: UN panel con el presupuesto (= suma de los budgets
                    por campaña, read-only: el apply lo arma con esa suma, editar el total no haría
                    nada) COMO ENCABEZADO del desglose por campaña (2026-07-24) — antes eran dos
                    bloques separados mostrando la misma plata. */}
                {item.action_type === 'CREATE_BU' && payload.campaigns_line
                    && (campaignEntries.length > 0 || deferredEntries.length > 0) ? (
                    // Lista UNIFICADA de las campañas del plan (2026-07-25): identificadas (✓, con su
                    // monto que suma al presupuesto de la línea) + pospuestas (⏳, con su monto del plan,
                    // atenuadas) en una sola vista, con el progreso en el encabezado.
                    <div className="mp-campaign-panel">
                        <div className="mp-campaign-panel-head">
                            <span className="mp-campaign-panel-title">{t(`${NS}.plan_campaigns_title`)}</span>
                            <span className="mp-campaign-panel-progress">
                                {t(`${NS}.plan_campaigns_progress`, {
                                    identified: matched, total: planTotal,
                                    sumA: formatBudgetWithCurrency(identifiedSum, payload.currency),
                                    sumB: formatBudgetWithCurrency(planSum, payload.currency),
                                    interpolation: {escapeValue: false}})}
                            </span>
                        </div>
                        <div className="mp-campaign-panel-rows">
                            {campaignEntries.map((c, i) => (
                                <div key={`m${i}`} className="mp-campaign-goal">
                                    <FaIcon iconName="circle-check" className="mp-campaign-state -identified"/>
                                    <span className="mp-campaign-name">{c.name_raw}</span>
                                    <span className="mp-campaign-amount">
                                        {payload.currency} {formatGoalValue(c.monthly_amount)}</span>
                                    {(c.goals || []).length > 0 && (
                                        <span className="mp-campaign-metrics">
                                            {c.goals.map((g) => (
                                                g.value != null ? `${g.metric_name} ${formatGoalValue(g.value)}` : g.metric_name
                                            )).join(' · ')}
                                        </span>
                                    )}
                                </div>
                            ))}
                            {deferredEntries.map((u, i) => {
                                // Des-posponer: si su duda tiene campañas reales para ofrecer, un botón
                                // "Elegir campaña" despliega las opciones inline (elegir una limpia el
                                // defer). Sin candidatas (catálogo de la cuenta agotado) no se ofrece.
                                const dq = deferredQuestionFor(u);
                                const reassignOpts = dq
                                    ? (dq.question.options || []).filter((o) => o.key !== DEFER_KEY) : [];
                                // Solo en cards PENDIENTES: responder regenera la propuesta y la anterior
                                // queda STALE → en una card ya aprobada se perdería la aprobación sin aviso.
                                const canReassign = isPending && reassignOpts.length > 0;
                                const reassignOpen = dq && !!expandedDeferred[dq.id];
                                return (
                                    <React.Fragment key={`d${i}`}>
                                        <div className="mp-campaign-goal -deferred">
                                            <FaIcon iconName="clock" className="mp-campaign-state -deferred"/>
                                            <span className="mp-campaign-name">{u.name_raw}</span>
                                            <span className="mp-campaign-amount">
                                                {payload.currency} {formatGoalValue(u.monthly_amount)}</span>
                                            <span className="mp-campaign-deferred-tag">
                                                {t(`${NS}.deferred_badge`)}
                                                <span className="info-icon-container mp-campaign-deferred-info">
                                                    <FaIcon iconName="info-circle"/>
                                                    <span className="warning-tooltip -below-right -rich">
                                                        <span className="warning-tooltip-body">
                                                            <span className="warning-tooltip-text">
                                                                {t(`${NS}.defer_tooltip`)}
                                                            </span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </span>
                                            {canReassign && (
                                                <button type="button" className="mp-campaign-reassign-btn"
                                                        disabled={regenerating}
                                                        aria-expanded={reassignOpen}
                                                        onClick={() => setExpandedDeferred(
                                                            (s) => ({...s, [dq.id]: !s[dq.id]}))}>
                                                    {t(`${NS}.reassign_deferred`)}
                                                    <FaIcon iconName={reassignOpen ? 'chevron-up' : 'chevron-down'}
                                                            className="mp-campaign-reassign-caret"/>
                                                </button>
                                            )}
                                        </div>
                                        {reassignOpen && canReassign && (
                                            <div className="mp-campaign-reassign-opts">
                                                <span className="mp-campaign-reassign-hint">
                                                    {t(`${NS}.reassign_deferred_hint`)}
                                                </span>
                                                <div className="mp-question-picks">
                                                    {reassignOpts.map((o) => renderQuestionOption(dq, o))}
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        {deferredSum > 0 && (
                            <p className="mp-campaign-panel-foot">
                                <FaIcon iconName="clock" className="mp-campaign-panel-foot-icon"/>
                                {t(`${NS}.plan_campaigns_deferred_foot`, {
                                    sum: formatBudgetWithCurrency(deferredSum, payload.currency),
                                    count: deferredEntries.length,
                                    interpolation: {escapeValue: false}})}
                            </p>
                        )}
                    </div>
                ) : null}

                {/* Presupuesto de la línea: editable (FOR_ALL: global, o campañas en progreso sin
                    matches — ahí el total sí aplica) o read-only (PER_CAMPAIGN que NO es campañas:
                    BU3 / modo clásico — el total es la suma por campaña). Las de campañas con matches
                    lo muestran en el panel de arriba. UPDATE_BU_CAMPAIGNS no lleva presupuesto. */}
                {isPending && APPLIABLE.has(item.action_type)
                    && item.action_type !== 'UPDATE_BU_CAMPAIGNS'
                    && item.action_type !== 'UPDATE_GOALS'
                    // PR-5: re-sincroniza montos POR CAMPAÑA; el presupuesto de la LÍNEA no es lo
                    // suyo. Su payload no trae `bu_type`, así que la condición de abajo daba
                    // `true` y el bloque se dibujaba en su rama EDITABLE, con «Presupuesto
                    // asignado» vacío y el configurador de tramos. Entrar a `APPLIABLE` sin entrar
                    // acá es el gemelo que faltaba: son DOS listas a mano (C-mcpagus-10).
                    && item.action_type !== 'UPDATE_CAMPAIGN_BUDGETS'
                    && !(payload.campaigns_line && payload.bu_type === 'TYPE_ONE_BUDGET_PER_CAMPAIGN') && (
                    payload.bu_type === 'TYPE_ONE_BUDGET_PER_CAMPAIGN' ? (
                        <div className="mp-budget-readonly">
                            <span className="mp-budget-edit-label">
                                {t(`${NS}.budget_label`)}{payload.currency ? ` (${payload.currency})` : ''}
                            </span>
                            <span className="mp-budget-readonly-value">
                                {formatBudgetWithCurrency(identifiedSum, payload.currency)}
                            </span>
                            <span className="mp-budget-readonly-note">
                                {t(`${NS}.budget_from_campaigns`, {count: matched})}
                            </span>
                        </div>
                    ) : (
                        // MISMO bloque que el alta/edición de la línea (feedback fede 2026-07-30):
                        // "Presupuesto asignado" + selector de moneda (fijo: la del plan, D9 nunca
                        // se convierte) + monto, y DEBAJO el configurador REAL de tramos
                        // ("Quiero definir tramos de inversión dentro del período"), pre-cargado
                        // con los del plan y editable — las ediciones viajan en el patch del
                        // approve con el mismo helper del alta de BU.
                        // La moneda va en el rótulo y no en una caja gris de sólo lectura (UD-2: se
                        // leía como un control roto); se cambia una vez para todo el plan, arriba.
                        // Miles con punto, como el monto del encabezado de la fila.
                        <div className="mp-budget-edit -canonical">
                            <label className="form-label">
                                {t('business_unit.assigned_budget')}{payload.currency ? ` (${payload.currency})` : ''}
                            </label>
                            <div className="row g-3">
                                <div className="col-md-7">
                                    <BudgetValueInput
                                        budgetAmount={budgetAmount}
                                        budgetCurrency={payload.currency || ''}
                                        hideCurrency
                                        numberLocale="es-AR"
                                        onAmountChange={(amount) => setEditedBudgets({...editedBudgets, [item.id]: amount})}/>
                                </div>
                            </div>
                            <BudgetPartialGoalsConfiguration
                                key={`mp-pg-${item.id}`}
                                enabled={(payload.budget_partial_goals || []).length > 0}
                                budgetPeriodType="PERIODIC"
                                budgetFromDate={monthFirstDay}
                                budgetToDate={monthLastDay}
                                budgetPartialGoals={partialGoalRowsOf(payload)}
                                onConfigurationChange={(cfg) => setEditedPartialGoals(
                                    {...editedPartialGoals, [item.id]: cfg})}
                                budgetTotalAmount={Number(budgetAmount) || payload.allocated_budget}
                                allowDailyRangeMode={false}/>
                        </div>
                    )
                )}

                {/* UPDATE_BU_CAMPAIGNS (2026-07-22): las campañas identificadas después del apply
                    que se van a SUMAR a la línea, con su presupuesto del plan. */}
                {item.action_type === 'UPDATE_BU_CAMPAIGNS' && (payload.additions || []).length > 0 && (
                    <div className="mp-campaign-goals">
                        <span className="mp-goals-label">{t(`${NS}.additions_label`)}</span>
                        {payload.additions.map((a, i) => (
                            <div key={i} className="mp-campaign-goal">
                                <span className="mp-campaign-name">{a.name_raw}</span>
                                <span className="mp-campaign-amount">
                                    {payload.currency} {formatGoalValue(a.monthly_amount)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* UPDATE_CAMPAIGN_BUDGETS (PR-5, 2026-09-13): el monto que el plan cambió, campaña
                    por campaña. El ANTES es lo que hace revisable al item: acá el plan pisa lo que
                    esté cargado (decisión de fede), así que quien aprueba tiene que ver qué se va. */}
                {item.action_type === 'UPDATE_CAMPAIGN_BUDGETS' && (payload.budget_updates || []).length > 0 && (
                    <div className="mp-campaign-goals">
                        <span className="mp-goals-label">{t(`${NS}.budget_updates_label`)}</span>
                        {payload.budget_updates.map((u, i) => (
                            <div key={i} className="mp-campaign-goal">
                                <span className="mp-campaign-name">{u.name_raw}</span>
                                <span className="mp-campaign-amount">
                                    {payload.currency} {formatGoalValue(u.current_amount)}
                                    {' \u2192 '}
                                    {payload.currency} {formatGoalValue(u.monthly_amount)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* F3 split (modo clásico / BU3): objetivos POR campaña. Las de campañas del split
                    ya los muestran en `mp-campaign-panel` de arriba → acá solo las demás. */}
                {item.action_type === 'CREATE_BU' && hasCampaignGoals && !payload.campaigns_line && (
                    <div className="mp-campaign-goals">
                        {campaignEntries.map((c, i) => (
                            <div key={i} className="mp-campaign-goal">
                                <span className="mp-campaign-name">{c.name_raw}</span>
                                <span className="mp-campaign-amount">{payload.currency} {formatGoalValue(c.monthly_amount)}</span>
                                {(c.goals || []).length > 0 && (
                                    <span className="mp-campaign-metrics">
                                        {c.goals.map((g) => (
                                            g.value != null ? `${g.metric_name} ${formatGoalValue(g.value)}` : g.metric_name
                                        )).join(' · ')}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {item.action_type === 'CREATE_BU' && (payload.goals || []).length > 0 && (
                    <div className="mp-goals">
                        <span className="mp-goals-label">{t(`${NS}.goals_label`)}</span>
                        {/* Un solo hint por card (no por objetivo): cuando ≥1 goal tiene competencia
                            objetivo-vs-real, explica una vez de qué se trata el radio de abajo. */}
                        {isPending && payload.goals.some((g) => (g.candidates || []).length > 0) && (
                            <span className="mp-goals-hint">{t(`${NS}.goal_ambiguous_hint`)}</span>
                        )}
                        <div className="mp-goals-list">
                            {payload.goals.map((g, i) => renderGoal(item, g, i, isPending))}
                            {/* Paridad con el editor: con UN solo objetivo del plan, la fila
                                "Objetivo #2" apagada permite sumar el segundo a mano. */}
                            {isPending && payload.goals.length === 1 && renderAddedGoalRow(item)}
                        </div>
                    </div>
                )}

                {/* Re-sync de OBJETIVOS (Fase B, UPDATE_GOALS): el diff de metas del plan sobre una
                    línea ya existente — cambios a nivel línea + por campaña + los no aplicados por el
                    tope de 2 objetivos. Aditivo: si el payload no trae un campo, esa parte no aparece. */}
                {item.action_type === 'UPDATE_GOALS' && renderGoalsUpdate(item, payload)}

                {/* Ritmo de inversión (tramos / BudgetPartialGoal) de un item YA REVISADO: solo
                    lectura, con el mismo lenguaje del configurador. En un item PENDIENTE los tramos
                    se ven y EDITAN en el configurador real, dentro del bloque de presupuesto. */}
                {!isPending && (payload.budget_partial_goals || []).length > 0 && (
                    <div className="mp-partial-goals">
                        <span className="mp-partial-goals-label">{t(`${NS}.partial_goals_label`)}</span>
                        {/* Mismo lenguaje visual que el configurador real de tramos de la línea
                            ("1. Al día 19 quiero haber invertido 70%"): filas numeradas en vez de una
                            frase suelta, para que se reconozca como lo mismo que después se ve —y se
                            edita— en la configuración de la BU. Acá es solo lectura (previsualización
                            de lo que el plan va a aplicar), por eso no se replican los controles. */}
                        <ol className="mp-partial-goals-list">
                            {payload.budget_partial_goals.map((pg, i) => (
                                <li key={i} className="mp-partial-goal">
                                    {t(`${NS}.partial_goal_row`, {
                                        day: checkpointDay(pg.date),
                                        percent: formatGoalValue(pg.value),
                                        interpolation: {escapeValue: false},
                                    })}
                                </li>
                            ))}
                        </ol>
                    </div>
                )}

                {item.review_status === 'FAILED' && item.apply_error && (
                    <span className="mp-change-error">{item.apply_error}</span>
                )}
                {/* CONFLICT: dos clases distintas (se distinguen por `payload.kind`). GOAL_MISMATCH
                    (Fase B) es un aviso de objetivo pisado a mano → tarjeta ámbar propia, más abajo.
                    El resto (CURRENCY_MISMATCH y propuestas viejas sin `kind`) es el hint de moneda. */}
                {item.action_type === 'CONFLICT' && payload.kind === 'GOAL_MISMATCH' && (
                    renderGoalMismatch(item, payload)
                )}
                {item.action_type === 'CONFLICT' && payload.kind !== 'GOAL_MISMATCH' && (
                    renderCurrencyConflict(item, payload)
                )}

                {isPending && APPLIABLE.has(item.action_type) && !opts.hideApproveFooter && (
                    <div className="mp-change-footer">
                        {/* Aprobar/Rechazar quedan bloqueados hasta decidir el destino de la línea
                            (vincular / crear nueva): la decisión de destino precede a la aprobación.
                            El hint explica por qué el botón está deshabilitado. */}
                        <div className="mp-change-buttons">
                            {hasLinkDecision && (
                                <span className="mp-change-buttons-hint">{t(`${NS}.decide_link_first`)}</span>
                            )}
                            {identificationPending && !hasLinkDecision && (
                                // Gate de resolución completa (2026-07-22): mismo patrón que el
                                // bloqueo por decisión de destino. Rechazar queda habilitado.
                                <span className="mp-change-buttons-hint">
                                    {t(`${NS}.identify_or_defer_first`, {count: pendingCount})}
                                </span>
                            )}
                            {nameIsBlank(item) && (
                                <span className="mp-change-buttons-hint">{t(`${NS}.name_required`)}</span>
                            )}
                            {needsCurrency && (
                                <span className="mp-change-buttons-hint">{t(`${NS}.currency_first`)}</span>
                            )}
                            {basisAsk && !needsCurrency && (
                                <span className="mp-change-buttons-hint">{t('media_plan.basis.basis_first')}</span>
                            )}
                            <button type="button" className="btn -alt mp-btn-sm"
                                    disabled={approveDisabled}
                                    onClick={() => review(item, 'approve')}>
                                {t(`${NS}.approve`)}
                            </button>
                            <button type="button" className="btn -alt -negative mp-btn-sm"
                                    disabled={busyItemId === item.id || hasLinkDecision || regenerating}
                                    onClick={() => review(item, 'reject')}>
                                {t(`${NS}.reject`)}
                            </button>
                        </div>
                    </div>
                )}

                {/* Footer del aviso de conflicto de objetivo (GOAL_MISMATCH): radio A/B "usar el del plan
                    vs mantener el mío". El backend aplica la decisión y realinea el baseline (el conflicto
                    no reaparece). Default = mantener el mío (P1-9: el plan nunca pisa al humano). */}
                {item.action_type === 'CONFLICT' && payload.kind === 'GOAL_MISMATCH' && isPending &&
                    renderGoalConflictResolution(item, payload)}
            </>
        );
    }

    // ─── Acordeón UX 2.1 ────────────────────────────────────────────────────────────
    // Estado de una fila como chip orientado a la ACCIÓN (no al nombre técnico del review_status):
    // qué te falta hacer con esta línea. Color = urgencia (rojo reservado a "Falló"). Ícono + texto
    // (nunca solo color). Los sub-estados "Con conflicto" y "Falta identificar" se derivan de flags
    // del payload sobre un item PENDING_REVIEW — no son estados nuevos del backend.
    function rowStatus(item) {
        const rs = item.review_status;
        if (rs === 'PENDING_REVIEW') {
            if (item.action_type === 'CONFLICT') {
                return {tone: 'conflict', icon: 'triangle-exclamation',
                        label: t(`${NS}.row_status.conflict`)};
            }
            const pending = isGatedItem(item) ? pendingUnmatched(item.payload).length : 0;
            if (pending > 0) {
                return {tone: 'identify', icon: 'circle-half-stroke',
                        label: t(`${NS}.row_status.identify`, {count: pending})};
            }
            return {tone: 'review', icon: 'circle', label: t(`${NS}.row_status.review`)};
        }
        const map = {
            APPROVED: {tone: 'approved', icon: 'circle-check', key: 'approved'},
            APPLIED: {tone: 'applied', icon: 'circle-check', key: 'applied'},
            REJECTED: {tone: 'muted', icon: 'circle-xmark', key: 'rejected'},
            FAILED: {tone: 'failed', icon: 'triangle-exclamation', key: 'failed'},
            STALE: {tone: 'muted', icon: 'ban', key: 'stale'},
        };
        const m = map[rs] || {tone: 'muted', icon: 'circle', key: rs.toLowerCase()};
        return {tone: m.tone, icon: m.icon,
                label: t(`${NS}.row_status.${m.key}`, {defaultValue: rs})};
    }

    // Título de la fila colapsada (1er renglón). CREATE_BU: el nombre del payload ya trae plataforma +
    // «(global)»/«(campañas)» → alcanza. El resto antepone el verbo de la acción al nombre de la línea.
    function rowTitle(item) {
        // El nombre EDITADO, no el del plan: si el header siguiera diciendo el viejo mientras el campo
        // de abajo muestra el nuevo, no habría forma de saber cuál se va a aplicar.
        const name = effectiveName(item);
        if (item.action_type === 'CREATE_BU') {
            // Quita el prefijo redundante "Media plan · " del nombre del split (ya estás en la tab
            // Media plan): "Media plan · Google (global)" → "Google (global)". Sin " · " queda igual.
            const sep = name.indexOf(' · ');
            const clean = sep >= 0 ? name.slice(sep + 3) : name;
            return clean || t(`${NS}.action.CREATE_BU`, {defaultValue: item.action_type});
        }
        // El item que sólo suma la cuenta DV360 a la línea (sin campañas) no «suma campañas del plan»:
        // se titula con la acción de la oferta.
        const payload = item.payload || {};
        const accountOnly = item.action_type === 'UPDATE_BU_CAMPAIGNS' && payload.dv360_offer
            && !(payload.additions || []).length;
        const verb = accountOnly
            ? t('media_plan.untracked.dv360_offer_action')
            : t(`${NS}.action.${item.action_type}`, {defaultValue: item.action_type});
        return name ? `${verb}: ${name}` : verb;
    }

    // Resumen de la fila colapsada (2do renglón, gris): la "información principal" de un pantallazo —
    // monto + objetivo(s) + progreso de campañas, según el tipo. Recalcula lo mínimo desde el payload
    // (barato; no toca la maquinaria de estado). Devuelve '' si no hay nada relevante que resumir.
    function rowSummary(item) {
        const payload = item.payload || {};
        const cur = payload.currency;
        const parts = [];
        const goalsText = (goals) => (goals || []).slice(0, 2).map((g) => {
            const v = g.value != null ? g.value : ((g.candidates || [])[0] || {}).value;
            return v != null ? `${g.metric_name} ${formatGoalValue(roundGoalValue(v))}` : g.metric_name;
        }).filter(Boolean).join(' · ');   // una métrica sin nombre no debe colar `undefined` en el join
        if (item.action_type === 'UPDATE_BU_BUDGET') {
            const b = item.current_state || {};
            // Sin ambos montos, "X → Y" quedaría como una flecha pelada: mejor no resumir nada.
            if (b.allocated_budget != null && payload.allocated_budget != null) {
                parts.push(`${formatBudgetWithCurrency(b.allocated_budget, cur)} → ${formatBudgetWithCurrency(payload.allocated_budget, cur)}`);
            }
        } else if (item.action_type === 'CREATE_BU' && payload.campaigns_line) {
            const matched = (payload.accounts || []).reduce((n, a) => n + ((a.campaigns || []).length), 0)
                + (payload.additions || []).length;
            const total = matched + (payload.unmatched_campaigns || []).length;
            const identifiedSum = (payload.accounts || []).flatMap((a) => a.campaigns || [])
                .reduce((s, c) => s + (Number(c.monthly_amount) || 0), 0)
                + (payload.additions || []).reduce((s, a) => s + (Number(a.monthly_amount) || 0), 0);
            const planSum = identifiedSum + (payload.unmatched_campaigns || [])
                .reduce((s, u) => s + (Number(u.monthly_amount) || 0), 0);
            if (planSum > 0) {
                parts.push(`${formatBudgetWithCurrency(identifiedSum, cur)} / ${formatBudgetWithCurrency(planSum, cur)}`);
            }
            if (total > 0) parts.push(t(`${NS}.row.camp_progress`, {identified: matched, total,
                interpolation: {escapeValue: false}}));
        } else if (item.action_type === 'CREATE_BU') {
            // Alcance explícito de la línea global (feedback Pablo 2026-07-29): "todas las campañas
            // de la cuenta" — es uno de los dos datos que la gente siempre deja sin mirar. El gate
            // por `campaigns_line` ya lo dio la rama anterior; se excluye la BU3 (`advisory`, mide
            // varias cuentas) y cualquier card con campañas seleccionadas (por si el shape cambia).
            if (!payload.advisory && (payload.accounts || []).every((a) => !(a.campaigns || []).length)) {
                parts.push(t(`${NS}.row.all_campaigns`));
            }
            if (payload.allocated_budget != null) {
                parts.push(t(`${NS}.row.per_month`, {
                    amount: formatBudgetWithCurrency(payload.allocated_budget, cur),
                    interpolation: {escapeValue: false}}));
            }
            const gt = goalsText(payload.goals);
            if (gt) parts.push(gt);
        } else if (item.action_type === 'UPDATE_GOALS') {
            const n = (payload.bu_changes || []).length
                + (payload.campaign_updates || []).reduce((s, c) => s + ((c.changes || []).length), 0);
            if (n > 0) parts.push(t(`${NS}.row.goal_changes`, {count: n}));
        } else if (item.action_type === 'UPDATE_BU_CAMPAIGNS') {
            const n = (payload.additions || []).length;
            if (n > 0) parts.push(t(`${NS}.row.add_campaigns`, {count: n}));
        } else if (item.action_type === 'UPDATE_CAMPAIGN_BUDGETS') {
            // PR-5 (2026-09-13): el re-sync de montos por campaña. El resumen dice CUÁNTAS cambian
            // y no la suma: el item existe porque cambió el REPARTO, y un total puede ser idéntico
            // antes y después mientras cada campaña se movió.
            const n = (payload.budget_updates || []).length;
            if (n > 0) parts.push(t(`${NS}.row.budget_updates`, {count: n}));
        } else if (item.action_type === 'CONFLICT') {
            if (payload.metric_name) parts.push(payload.metric_name);
        }
        return parts.join(' · ');
    }

    // Una fila del acordeón (UX 2.1): header colapsable (barra de canal + logo + título + resumen +
    // chip de estado) y, si está expandida, el cuerpo completo (`renderItemInner`). Exclusivo: abrir
    // una contrae el resto.
    function renderAccordionRow(item, index) {
        const st = rowStatus(item);
        const identity = rowIdentity(item);
        const expanded = identity === expandedIdentity;
        const channel = PLATFORM_TO_CHANNEL[cardPlatform(item)];   // logo de canal a color (ChannelLogo)
        // Barra lateral por CANAL (mismo lenguaje que Monitoreo): color sólido del canal; dashed violeta
        // ("mixta") para la línea de objetivo global cross-plataforma (BU3, `advisory`). El estado NO va
        // en la barra (lo dice el chip) → la barra queda libre para el canal, como en el resto del front.
        const barClass = (item.payload || {}).advisory ? '-mixed'
            : channel ? `-chan-${channel.toLowerCase()}` : '';
        const summary = rowSummary(item);
        const lowConfidence = item.confidence && item.confidence !== 'HIGH';
        // Aprobar/Rechazar en el HEADER cuando la fila está lista (nada por resolver primero): la acción
        // se ve de un vistazo, sin tener que abrir y bajar hasta el pie del panel. Si no está lista, el
        // chip de estado dice qué falta y la acción vive adentro (footer con su hint de bloqueo).
        // `canApprove` decide si los botones EXISTEN en el encabezado; el nombre vacío no entra acá,
        // porque un botón que se esfuma mientras escribís no explica nada (y se llevaba puesto
        // «Rechazar», que con el nombre no tiene nada que ver). Se muestran y se deshabilitan, que
        // es lo que ya hacía el pie de la misma fila.
        // Sin la moneda del plan, o con una tabla sin base decidida, no se aprueba nada: los botones se
        // van al pie, con el motivo.
        const canApprove = rowReadyToApprove(item) && !needsCurrency && !basisAsk;
        const blockedByName = nameIsBlank(item);
        const busy = busyItemId === item.id;
        return (
            // `key` por IDENTIDAD y no por PK: una regeneración cambia todos los ids, y con el id
            // como key React desmontaba y volvía a montar la lista entera — se re-disparaba la
            // animación `mp-rise` de cada fila y el navegador perdía todo anclaje de scroll. Con la
            // identidad reconcilia, así que el nodo de la fila abierta SOBREVIVE y se lo puede medir
            // para devolverla a su lugar (`anchorExpandedRow`).
            <li key={identity}
                ref={(el) => {
                    if (el) rowNodes.current[identity] = el;
                    else delete rowNodes.current[identity];
                }}
                className={`mp-row -${item.review_status.toLowerCase()} ${barClass} ${expanded ? '-expanded' : ''}`}
                style={{animationDelay: `${Math.min(index, 12) * 40}ms`}}>
                <div className="mp-row-head">
                    <button type="button" className="mp-row-toggle"
                            onClick={() => toggleRow(item, index)}
                            aria-expanded={expanded} aria-controls={`mp-row-body-${item.id}`}>
                        <FaIcon iconName={expanded ? 'chevron-down' : 'chevron-right'} className="mp-row-caret"/>
                        {channel && (
                            <span className="mp-row-plat">
                                {/* hspace/vspace/marginLeft/padding en 0: los defaults de ChannelLogo
                                    (10px + marginLeft propio por canal) engordan la fila y corren el
                                    título de Meta/TikTok respecto de Google. Mismo patrón que
                                    PlatformPill/ChatHeader para logos compactos. */}
                                <ChannelLogo channel={channel} useOld={false} renderVeryColoredVersion
                                             width="20px" height="20px"
                                             hspace={0} vspace={0} marginLeft={0} padding={0}/>
                            </span>
                        )}
                        <span className="mp-row-titles">
                            <span className="mp-row-title">{rowTitle(item)}</span>
                            {summary && <span className="mp-row-summary">{summary}</span>}
                        </span>
                        {lowConfidence && (
                            <span className={`mp-confidence ${CONFIDENCE_CLASS[item.confidence] || ''}`}>
                                {t(`${NS}.confidence.${item.confidence.toLowerCase()}`, {defaultValue: item.confidence})}
                            </span>
                        )}
                        <span className={`mp-row-chip -${st.tone}`}>
                            <FaIcon iconName={st.icon} className="mp-row-chip-icon"/>
                            {st.label}
                        </span>
                    </button>
                    {canApprove && (
                        <div className="mp-row-actions">
                            {/* Mientras la acción está en vuelo, el `SpinnerButton` canónico ocupa el
                                lugar de los botones: atenuarlos y nada más dejaba la pantalla sin
                                ninguna señal de progreso, y un approve puede tardar. */}
                            {busy ? <SpinnerButton/> : (
                                <>
                                    <button type="button" className="btn -alt mp-btn-sm"
                                            disabled={blockedByName || regenerating}
                                            title={blockedByName ? t(`${NS}.name_required`) : undefined}
                                            onClick={() => review(item, 'approve')}>
                                        {t(`${NS}.approve`)}
                                    </button>
                                    <button type="button" className="btn -alt -negative mp-btn-sm"
                                            disabled={regenerating}
                                            onClick={() => review(item, 'reject')}>
                                        {t(`${NS}.reject`)}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {expanded && (
                    <div className="mp-row-body" id={`mp-row-body-${item.id}`}>
                        {renderItemInner(item, {inAccordion: true, hideApproveFooter: canApprove})}
                    </div>
                )}
            </li>
        );
    }

    // Bloque de decisión de destino de una card de CREATE con candidatas de vinculación (§3.1). Aviso
    // + candidata sugerida con evidencia (campañas en común + presupuesto) + radio de 3 opciones:
    // vincular con la sugerida (pre-marcada si `suggested`), crear nueva (pre-marcada si no), o
    // vincular con otra línea (solo si hay >1 candidata → progressive disclosure). Confirmar dispara
    // el answer (pinnea el destino + regenera). Tono informativo (sin rojo): es una sugerencia.
    function renderLinkDecision(item, candidates) {
        const suggested = candidates.find((c) => c.suggested) || null;
        const primary = suggested || candidates[0];   // la mostrada "arriba" del bloque
        const others = candidates.filter((c) => c !== primary);
        // Default del radio (misma fuente que confirmLink): la sugerida pre-marcada si la hay, sino
        // "crear nueva". Así confirmar sin tocar el radio manda exactamente lo que está pre-marcado.
        const defaultKey = defaultLinkAnswerKey(item);
        const choice = linkChoices[item.id] !== undefined ? linkChoices[item.id] : defaultKey;
        const setChoice = (key) => setLinkChoices({...linkChoices, [item.id]: key});
        const busy = busyItemId === item.id;
        const othersOpen = !!linkOptionsOpen[item.id];
        const planCurrency = (item.payload || {}).currency;
        // La primaria como opción de radio (nombre + evidencia). Reutilizada para las "otras" líneas.
        const renderCandidateOption = (c) => {
            const key = `link::${c.bu_id}`;
            const selected = choice === key;
            // El plan viene en una moneda; una BU candidata en OTRA no se puede vincular sin generar
            // un conflicto de moneda (el backend nunca la sugiere, pero puede elegirse a mano). Se
            // avisa acá — antes solo mostraba "ARS 222.222" en un plan USD, sin alarma (2026-07-24).
            const currencyMismatch = c.currency && planCurrency && c.currency !== planCurrency;
            return (
                <label key={c.bu_id} className={`mp-link-option ${selected ? '-selected' : ''}`}>
                    <input type="radio" className="mp-link-radio-input" name={`link-${item.id}`}
                           checked={selected} disabled={busy} onChange={() => setChoice(key)}/>
                    <span className="mp-link-radio" aria-hidden="true"/>
                    <span className="mp-link-option-body">
                        <span className="mp-link-option-main">
                            <span className="mp-link-option-name">
                                {t(`${NS}.link.option_link`, {name: c.name,
                                    interpolation: {escapeValue: false}})}
                            </span>
                            {c.suggested && (
                                <span className="mp-link-suggested">{t(`${NS}.link.suggested`)}</span>
                            )}
                            {currencyMismatch && (
                                <span className="mp-link-currency-warn">
                                    <FaIcon iconName="triangle-exclamation"/>
                                    {t(`${NS}.link.currency_mismatch`, {currency: c.currency,
                                        interpolation: {escapeValue: false}})}
                                </span>
                            )}
                        </span>
                        <span className="mp-link-option-evidence">
                            {c.match_count > 0 && (
                                <span>{t(`${NS}.link.match_count`, {count: c.match_count})}</span>
                            )}
                            {c.allocated_budget != null && (
                                <span className={currencyMismatch ? '-warn' : ''}>
                                    {formatBudgetWithCurrency(c.allocated_budget, c.currency)}</span>
                            )}
                            {/* La BU monitorea la cuenta entera (sin selección de campañas): la
                                decisión de vincular es informada del alcance actual (§5). Campo
                                opcional del backend → degrada sin romper en propuestas viejas. */}
                            {c.includes_all_campaigns === true && (
                                <span>{t(`${NS}.link.includes_all_campaigns`)}</span>
                            )}
                        </span>
                    </span>
                </label>
            );
        };
        return (
            <div className="mp-link-decision">
                <div className="mp-link-decision-head">
                    <FaIcon iconName="link" className="icon"/>
                    <span>{t(`${NS}.link.title`)}</span>
                </div>
                <div className="mp-link-options">
                    {renderCandidateOption(primary)}

                    <label className={`mp-link-option ${choice === 'create_new' ? '-selected' : ''}`}>
                        <input type="radio" className="mp-link-radio-input" name={`link-${item.id}`}
                               checked={choice === 'create_new'} disabled={busy}
                               onChange={() => setChoice('create_new')}/>
                        <span className="mp-link-radio" aria-hidden="true"/>
                        <span className="mp-link-option-body">
                            <span className="mp-link-option-name">{t(`${NS}.link.option_create`)}</span>
                        </span>
                    </label>

                    {/* "Vincular con otra línea…" — solo con >1 candidata: despliega las demás bajo
                        demanda (progressive disclosure, como el «+N opciones» de las dudas). */}
                    {others.length > 0 && (
                        othersOpen ? (
                            others.map((c) => renderCandidateOption(c))
                        ) : (
                            <button type="button" className="mp-link-more"
                                    onClick={() => setLinkOptionsOpen({...linkOptionsOpen, [item.id]: true})}>
                                {t(`${NS}.link.other_line`, {count: others.length})}
                                <FaIcon iconName="chevron-down" className="mp-link-more-caret"/>
                            </button>
                        )
                    )}
                </div>
                {/* Gobierno del plan al vincular (§4.4): al elegir un vínculo, el presupuesto de la
                    línea —y sus tramos de pacing si el plan trae cambios intra-mes— pasan a
                    actualizarse desde el plan. Nota discreta (sin ícono de alarma), solo cuando la
                    opción efectiva es un vínculo. */}
                {choice.startsWith('link::') && (
                    <p className="mp-link-governance">{t(`${NS}.link.governance_note`)}</p>
                )}
                <div className="mp-link-actions">
                    {/* Botón DINÁMICO según el radio (feedback Pablo 2026-07-29: "nadie entiende
                        qué es destino") — el botón dice lo que va a hacer, no un término técnico. */}
                    <button type="button" className="btn -alt mp-btn-sm" disabled={busy}
                            onClick={() => confirmLink(item)}>
                        {busy ? t(`${NS}.link.confirming`)
                            : choice.startsWith('link::')
                                ? t(`${NS}.link.confirm_link`)
                                : t(`${NS}.link.confirm_create`)}
                    </button>
                </div>
            </div>
        );
    }

    // Un cambio de objetivo (bu_change / campaign change) como una línea legible. ADD → "+ CPA 19,6";
    // UPDATE → "ROAS 8 → 10,5"; REMOVE → "− CPA · lo quita el plan". Los valores se formatean con el
    // helper es-AR de goals (redondeo por magnitud). Devuelve el <span> con el ícono del tipo.
    function renderGoalChangeLine(change, key) {
        const kind = change.kind;
        const metric = change.metric_name;
        const from = formatGoalValue(roundGoalValue(change.from_value));
        const to = formatGoalValue(roundGoalValue(change.to_value));
        return (
            <span key={key} className={`mp-goal-change -${(kind || '').toLowerCase()}`}>
                {kind === 'ADD' && (
                    <>
                        <span className="mp-goal-change-op" aria-hidden="true">+</span>
                        <span className="mp-goal-change-metric">{metric}</span>
                        <span className="mp-goal-change-value">{to}</span>
                    </>
                )}
                {kind === 'UPDATE' && (
                    <>
                        <span className="mp-goal-change-metric">{metric}</span>
                        <span className="mp-goal-change-value">{from}</span>
                        <FaIcon iconName="arrow-right" className="mp-goal-change-arrow"/>
                        <span className="mp-goal-change-value -to">{to}</span>
                    </>
                )}
                {kind === 'REMOVE' && (
                    <>
                        <span className="mp-goal-change-op -remove" aria-hidden="true">−</span>
                        <span className="mp-goal-change-metric">{metric}</span>
                        <span className="mp-goal-change-removed">{t(`${NS}.goal_update.removed`)}</span>
                    </>
                )}
            </span>
        );
    }

    // Re-sync de OBJETIVOS (Fase B, UPDATE_GOALS): el diff de metas del plan sobre una línea existente.
    // (1) `bu_changes` a nivel línea; (2) `campaign_updates` por campaña (colapsable si hay varias);
    // (3) `over_cap` = objetivos que el plan trae pero no se aplican por el tope de 2 por entidad.
    // Contrato ADITIVO: cada sección solo se renderiza si su campo viene en el payload.
    function renderGoalsUpdate(item, payload) {
        const buChanges = payload.bu_changes || [];
        const campaignUpdates = payload.campaign_updates || [];
        const overCap = payload.over_cap || [];
        const campaignsOpen = !!goalCampaignsOpen[item.id];
        // Con una sola campaña se muestra siempre expandido (no vale la pena plegar); con varias,
        // colapsa por default para no enterrar los cambios de la línea.
        const collapseCampaigns = campaignUpdates.length > 1;
        return (
            <div className="mp-goal-update">
                {buChanges.length > 0 && (
                    <div className="mp-goal-update-section">
                        <span className="mp-goals-label">{t(`${NS}.goal_update.bu_label`)}</span>
                        <div className="mp-goal-change-list">
                            {buChanges.map((c, i) => renderGoalChangeLine(c, i))}
                        </div>
                    </div>
                )}

                {campaignUpdates.length > 0 && (
                    <div className="mp-goal-update-section">
                        {collapseCampaigns ? (
                            <button type="button" className="mp-goal-update-camp-toggle"
                                    onClick={() => setGoalCampaignsOpen(
                                        (s) => ({...s, [item.id]: !s[item.id]}))}>
                                <FaIcon iconName={campaignsOpen ? 'chevron-up' : 'chevron-down'}
                                        className="mp-goal-update-camp-caret"/>
                                {t(`${NS}.goal_update.campaigns_label`, {count: campaignUpdates.length})}
                            </button>
                        ) : (
                            <span className="mp-goals-label">
                                {t(`${NS}.goal_update.campaigns_label`, {count: campaignUpdates.length})}
                            </span>
                        )}
                        {(!collapseCampaigns || campaignsOpen) && (
                            <div className="mp-goal-update-campaigns">
                                {campaignUpdates.map((cu, ci) => (
                                    <div key={ci} className="mp-goal-update-campaign">
                                        <span className="mp-goal-update-campaign-name">{cu.name_raw}</span>
                                        <div className="mp-goal-change-list">
                                            {(cu.changes || []).map((c, i) => renderGoalChangeLine(c, i))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* No aplicado por el tope de 2 objetivos por entidad: aviso discreto (no ámbar, no
                    bloquea) — el usuario sabe qué quedó afuera y por qué. */}
                {overCap.length > 0 && (
                    <p className="mp-goal-update-overcap">
                        <FaIcon iconName="info-circle" className="icon"/>
                        {t(`${NS}.goal_update.over_cap`, {
                            metrics: overCap.map((o) => o.metric_name).join(', '),
                            interpolation: {escapeValue: false},
                        })}
                    </p>
                )}
            </div>
        );
    }

    // Aviso de conflicto de objetivo (CONFLICT · GOAL_MISMATCH, Fase B V1): el plan quiere cambiar o
    // quitar un objetivo que el usuario editó a mano en Dashbo → NO se toca, solo se avisa. Tarjeta
    // ÁMBAR (aviso, no error crítico) — mismo lenguaje que los bloqueos, distinto del rojo de errores.
    // Conflicto de MONEDA (D9): la línea que ya existe mide en una moneda y el plan está en otra. Antes
    // decía «Resolvelo a mano» sin nada que hacer en la pantalla (UD-2). Casi siempre es la moneda del
    // plan la que estaba mal: la salida es un clic que la fija en la de la línea y rearma la propuesta.
    // Propuestas viejas sin `bu_currency` caen al texto genérico.
    function renderCurrencyConflict(item, payload) {
        if (!payload.bu_currency || !payload.plan_currency) {
            return <span className="mp-change-hint">{t(`${NS}.conflict_hint`)}</span>;
        }
        return (
            <div className="mp-currency-conflict">
                <span className="mp-change-hint">
                    {t(`${NS}.conflict_currency.text`, {
                        name: payload.bu_name || '', bu: payload.bu_currency, plan: payload.plan_currency,
                        interpolation: {escapeValue: false},
                    })}
                </span>
                {item.review_status === 'PENDING_REVIEW' && (
                    savingCurrency ? <SpinnerButton/> : (
                        <button type="button" className="btn -alt mp-btn-sm" disabled={regenerating}
                                onClick={() => changePlanCurrency(payload.bu_currency)}>
                            {t(`${NS}.conflict_currency.cta`, {bu: payload.bu_currency})}
                        </button>
                    )
                )}
            </div>
        );
    }

    function renderGoalMismatch(item, payload) {
        const scope = payload.level === 'CAMPAIGN' && payload.campaign_name
            ? t(`${NS}.goal_conflict.scope_campaign`, {campaign: payload.campaign_name,
                interpolation: {escapeValue: false}})
            : '';
        const mine = formatGoalValue(roundGoalValue(payload.manual_value));
        const planVal = formatGoalValue(roundGoalValue(payload.plan_value));
        return (
            <div className="mp-goal-conflict">
                <FaIcon iconName="triangle-exclamation" className="icon"/>
                <div className="mp-goal-conflict-body">
                    <span className="mp-goal-conflict-title">
                        {t(`${NS}.goal_conflict.title`, {bu: payload.name, scope,
                            interpolation: {escapeValue: false}})}
                    </span>
                    <span className="mp-goal-conflict-text"
                          dangerouslySetInnerHTML={{__html: payload.conflict_kind === 'REMOVE'
                              ? t(`${NS}.goal_conflict.body_remove`, {
                                  metric: payload.metric_name, mine,
                                  interpolation: {escapeValue: false}})
                              : t(`${NS}.goal_conflict.body_update`, {
                                  metric: payload.metric_name, mine, plan: planVal,
                                  interpolation: {escapeValue: false}})}}/>
                </div>
            </div>
        );
    }

    // Radio A/B para RESOLVER el conflicto de objetivo (GOAL_MISMATCH): "usar el del plan" (aplica el
    // valor/quita del plan y el plan vuelve a gobernar la métrica) vs "mantener el mío" (conserva el
    // valor manual; el conflicto no reaparece). Default = mantener el mío (P1-9). Reusa el radio
    // canónico `.mp-link-option`/`.mp-link-radio`.
    function renderGoalConflictResolution(item, payload) {
        const busy = busyItemId === item.id;
        const choice = conflictChoices[item.id] || 'keep_mine';
        const isRemove = payload.conflict_kind === 'REMOVE';
        const mine = formatGoalValue(roundGoalValue(payload.manual_value));
        const planVal = payload.plan_value != null
            ? formatGoalValue(roundGoalValue(payload.plan_value)) : '';
        const setChoice = (v) => setConflictChoices((prev) => ({...prev, [item.id]: v}));
        const option = (key, name) => (
            <label className={`mp-link-option ${choice === key ? '-selected' : ''}`}>
                <input type="radio" className="mp-link-radio-input" name={`conflict-${item.id}`}
                       checked={choice === key} disabled={busy} onChange={() => setChoice(key)}/>
                <span className="mp-link-radio" aria-hidden="true"/>
                <span className="mp-link-option-body">
                    <span className="mp-link-option-name"
                          dangerouslySetInnerHTML={{__html: name}}/>
                </span>
            </label>
        );
        return (
            <div className="mp-goal-conflict-resolve">
                <span className="mp-goal-conflict-resolve-label">
                    {t(`${NS}.goal_conflict.resolve_label`)}
                </span>
                <div className="mp-link-options">
                    {option('use_plan', isRemove
                        ? t(`${NS}.goal_conflict.use_plan_remove`,
                            {metric: payload.metric_name, interpolation: {escapeValue: false}})
                        : t(`${NS}.goal_conflict.use_plan_update`,
                            {metric: payload.metric_name, plan: planVal,
                             interpolation: {escapeValue: false}}))}
                    {option('keep_mine', t(`${NS}.goal_conflict.keep_mine`,
                        {metric: payload.metric_name, mine, interpolation: {escapeValue: false}}))}
                </div>
                <div className="mp-change-buttons">
                    {/* «Usar el del plan» escribe el objetivo en la línea: sin la moneda del plan
                        decidida, el valor podría estar en otra (review C-02; el backend también lo frena). */}
                    {needsCurrency && choice === 'use_plan' && (
                        <span className="mp-change-buttons-hint">{t(`${NS}.currency_first`)}</span>
                    )}
                    <button type="button" className="btn -alt mp-btn-sm"
                            disabled={busy || (needsCurrency && choice === 'use_plan')}
                            onClick={() => resolveConflict(item, choice)}>
                        {t(`${NS}.goal_conflict.resolve_confirm`)}
                    </button>
                </div>
            </div>
        );
    }

    // Un objetivo del CREATE. Ya revisado → línea compacta de solo lectura ("Objetivo 1° · CPA ·
    // 19.625"). PENDIENTE → la MISMA fila del editor de la línea de control (feedback Pablo/fede
    // 2026-07-30): Switch "Objetivo #N" + Métrica (MetricCombobox) + Valor (DecimalInput) — los
    // componentes canónicos, no una réplica. La competencia objetivo-vs-real del plan (goal
    // degradado con `candidates`) se resuelve con chips DEBAJO del valor que lo completan.
    function renderGoal(item, g, i, isPending) {
        const goalEdit = (editedGoals[item.id] || {})[i];
        const candidates = g.candidates || [];
        // Valor efectivo: edición del usuario > valor del plan > candidato sugerido (default del
        // radio), redondeado (el aplicado). Los candidatos se comparan por su valor canónico.
        const value = goalEdit !== undefined
            ? goalEdit
            : (g.value != null ? String(g.value)
                : (candidates.length > 0 ? String(roundGoalValue(candidates[0].value)) : ''));
        const setValue = (v) => setEditedGoals({
            ...editedGoals,
            [item.id]: {...(editedGoals[item.id] || {}), [i]: v},
        });

        if (!isPending) {
            // Nomenclatura del tablero (feedback Pablo 2026-07-29): "Objetivo 1°/2°", la misma de
            // las columnas del Dashboard. La prioridad ES el índice del array (contrato del apply).
            return (
                <div key={i} className="mp-goal -readonly">
                    <span className="mp-goal-priority">{t(`${NS}.goal_priority`, {number: i + 1})}</span>
                    <span className="mp-goal-metric">{g.metric_name}</span>
                    <span className="mp-goal-value">
                        {g.value != null ? formatGoalValue(g.value) : t(`${NS}.goal_no_target`)}
                    </span>
                </div>
            );
        }

        const enabled = !((disabledGoals[item.id] || {})[i]);
        const metricName = (editedGoalMetrics[item.id] || {})[i] !== undefined
            ? editedGoalMetrics[item.id][i] : g.metric_name;
        const toggleGoal = (event) => {
            const isOn = event.target.checked;
            setDisabledGoals({
                ...disabledGoals,
                [item.id]: {...(disabledGoals[item.id] || {}), [i]: !isOn},
            });
        };
        const setMetric = (name) => setEditedGoalMetrics({
            ...editedGoalMetrics,
            [item.id]: {...(editedGoalMetrics[item.id] || {}), [i]: name},
        });
        // El DecimalInput es no-controlado post-mount → un pick del radio A/B lo REMONTA con el
        // valor elegido (key con contador de picks); tipear no remonta (no pierde el foco).
        const pickCount = (goalPicks[item.id] || {})[i] || 0;
        const pickValue = (v) => {
            setValue(v);
            setGoalPicks({
                ...goalPicks,
                [item.id]: {...(goalPicks[item.id] || {}), [i]: pickCount + 1},
            });
        };
        const canon = (c) => String(roundGoalValue(c.value));
        // Los rótulos solo ayudan si difieren entre candidatos (en Rouge las 3 columnas comparten
        // header "Google ads" → no aportan y se ocultan; en otras "Objetivo"/"Real" sí).
        const labelsHelp = new Set(candidates.map((c) => c.column_label || '')).size > 1;

        return (
            <div key={i} className="mp-goal-config row mb-3 align-items-start">
                <div className="col-md-auto">
                    <div className="switch-input d-flex align-items-center">
                        <Switch centered={true} onChange={toggleGoal} checked={enabled}
                                name={`mp-goal-active-${item.id}-${i}`} style={{marginLeft: '0'}}/>
                        <div>{t('goal_configuration.goal_number', {number: i + 1})}</div>
                    </div>
                </div>
                <div className="col-md">
                    <div className="row g-3">
                        <div className="col-md-7">
                            <label>{t('goal_configuration.metric')}</label>
                            <MetricCombobox
                                name={`mp-goal-metric-${item.id}-${i}`}
                                value={enabled ? metricName : null}
                                disabled={!enabled}
                                customMetrics={customMetrics}
                                onChange={setMetric}/>
                        </div>
                        <div className="col-md-5">
                            <label>{t('goal_configuration.value')}</label>
                            <DecimalInput key={`mp-goal-value-${item.id}-${i}-${pickCount}`}
                                          name={`mp-goal-value-${item.id}-${i}`}
                                          readOnly={!enabled}
                                          onChange={setValue}
                                          value={value}/>
                        </div>
                    </div>
                    {/* Radio A/B objetivo-vs-real (HITL del media plan): completa el Valor de
                        arriba. El link ↗ abre la celda de la planilla de donde salió cada uno. */}
                    {enabled && candidates.length > 0 && (
                        <div className="mp-goal-options -under-config">
                            {candidates.map((c, ci) => {
                                const selected = canon(c) === value;
                                const evidence = {gid: (item.evidence || {}).gid, cell: c.cell};
                                const url = cellUrl(connection.external_id, evidence);
                                return (
                                    // El peek envuelve la opción ENTERA a propósito: `.mp-goal-option`
                                    // tiene `overflow:hidden` (comparte el border-radius con el link ↗)
                                    // y ahí adentro el popover quedaría recortado.
                                    // `hint={false}`: acá el peek envuelve el chip entero y son 2-3
                                    // por objetivo — un ojo en cada uno sería más ruido que ayuda.
                                    // La señal vive en el link de evidencia de la fila, que está
                                    // siempre presente; el gesto se aprende una vez.
                                    <CellPeek key={ci} agencyClientId={clientId}
                                              snapshotId={snapshot && snapshot.id}
                                              cell={peekCell(evidence)} hint={false}>
                                    <div className={`mp-goal-option ${selected ? '-selected' : ''}`}>
                                        <button type="button" className="mp-goal-option-pick"
                                                onClick={() => pickValue(canon(c))}>
                                            <span className="mp-goal-radio" aria-hidden="true"/>
                                            <span className="mp-goal-option-value">{formatGoalValue(c.value)}</span>
                                            {ci === 0 && (
                                                <span className="mp-goal-suggested">{t(`${NS}.goal_suggested`)}</span>
                                            )}
                                            {labelsHelp && c.column_label && (
                                                <span className="mp-goal-option-label">{c.column_label}</span>
                                            )}
                                        </button>
                                        {url && (
                                            <a className="mp-goal-option-cell" href={url}
                                               target="_blank" rel="noopener noreferrer"
                                               title={t(`${NS}.goal_view_cell`,
                                                   {cell: c.cell, interpolation: {escapeValue: false}})}>
                                                <FaIcon iconName="up-right-from-square"/>
                                            </a>
                                        )}
                                    </div>
                                    </CellPeek>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // "Qué va a medir esta línea": una CAJA POR CUENTA con la modalidad de selección de campañas y
    // el evento de conversión — los dos datos que, según Pablo, la gente siempre deja sin mirar. Se
    // muestran como bloque estructurado (no prosa) porque el usuario no lee los textos.
    //   · sin `campaigns` en la cuenta → INCLUDE_ALL: "Todas las campañas de la cuenta"
    //   · con `campaigns`             → selección explícita: N campañas, con sus nombres
    // El evento de conversión sale por cuenta en el CREATE (lo que la línea VA a medir) y del
    // payload en los UPDATE (lo que la BU real mide hoy).
    function renderLineScope(item, ctx) {
        const payload = item.payload || {};
        const isCreate = item.action_type === 'CREATE_BU';
        const accounts = payload.accounts || [];
        const buConversion = payload.conversion_events || null;
        if (!isCreate) {
            // UPDATE: no hay cuentas en el payload, solo lo que la BU real mide hoy.
            return buConversion ? (
                <div className="mp-line-scope">
                    <div className="mp-scope-account">
                        <div className="mp-scope-account-body">{renderConversionEvent(buConversion)}</div>
                    </div>
                </div>
            ) : null;
        }
        if (accounts.length === 0) return null;
        // Card gateada con 0 campañas identificadas: todavía no se sabe el alcance real (aspira a
        // ser por campaña) → se omite la modalidad para no afirmar "cuenta completa" en falso.
        const scopeUnknown = ctx.identificationPending && ctx.matched === 0;
        // Los NOMBRES de las campañas solo si ningún otro bloque de la card ya las lista: la línea
        // «(campañas)» tiene el panel "Campañas del plan" y el modo clásico su lista con objetivos.
        // La BU3 no tiene ninguno de los dos — es justo la card donde no se veía nada.
        const showNames = !payload.campaigns_line && !ctx.hasCampaignGoals;
        return (
            <div className="mp-line-scope">
                <span className="mp-line-scope-title">{t(`${NS}.scope_title`)}</span>
                {accounts.map((account, ai) => {
                    const campaigns = account.campaigns || [];
                    const wholeAccount = campaigns.length === 0;
                    const channel = PLATFORM_TO_CHANNEL[account.platform];
                    return (
                        <div key={ai} className="mp-scope-account">
                            <div className="mp-scope-account-head">
                                {channel && (
                                    <ChannelLogo channel={channel} useOld={false} renderVeryColoredVersion
                                                 width="16px" height="16px"
                                                 hspace={0} vspace={0} marginLeft={0} padding={0}/>
                                )}
                                <span className="mp-scope-account-name">{account.ad_account_name}</span>
                                {/* En qué moneda gasta la cuenta (UD-2): sin esto, un plan en USD sobre
                                    una cuenta en COP no se podía detectar mirando la propuesta. */}
                                {account.ad_account_currency && (
                                    <span className="mp-scope-chip -currency">
                                        {t(`${NS}.scope_currency`, {currency: account.ad_account_currency})}
                                    </span>
                                )}
                                {!scopeUnknown && (
                                    <span className={`mp-scope-chip ${wholeAccount ? '-account' : '-campaign'}`}>
                                        {wholeAccount
                                            ? t(`${NS}.scope_whole_account`)
                                            : t(`${NS}.scope_n_campaigns`, {count: campaigns.length})}
                                    </span>
                                )}
                            </div>
                            <div className="mp-scope-account-body">
                                {!scopeUnknown && (wholeAccount || showNames) && (
                                    <span className="mp-scope-account-detail">
                                        {wholeAccount
                                            ? t(`${NS}.scope_detail_whole_account`)
                                            : t(`${NS}.scope_detail_campaigns`, {
                                                names: campaigns.slice(0, 4).map((c) => c.name_raw).join(', '),
                                                interpolation: {escapeValue: false},
                                            })}
                                        {!wholeAccount && campaigns.length > 4
                                            && ` ${t(`${NS}.scope_detail_more`, {count: campaigns.length - 4})}`}
                                    </span>
                                )}
                                {renderConversionEvent(account.conversion_events)}
                                {account.ad_account_currency && payload.currency
                                    && account.ad_account_currency !== payload.currency && (
                                    <span className="mp-scope-account-detail">
                                        {t(`${NS}.scope_currency_converted`, {
                                            account: account.ad_account_currency, plan: payload.currency})}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // Evento de conversión de una cuenta (o de la BU en los UPDATE). Modos del backend:
    //   SELECTED               → la BU ya mide estos eventos (UPDATE sobre una línea existente).
    //   INHERITED              → Meta: el apply va a heredar los eventos que el cliente ya tiene
    //                            elegidos en esa cuenta → la línea nace midiendo eso. Sin alarma.
    //   ALL_PRIMARY_CONVERSIONS→ mide todas las primarias de la cuenta (Google/TikTok/LinkedIn):
    //                            ámbar informativo — mide, pero no hay evento específico.
    //   NONE_SELECTED          → Meta sin nada que heredar: NO va a medir conversiones, así que un
    //                            objetivo de CPA queda vacío. El copy más fuerte (ámbar igual: el
    //                            rojo se reserva a lo crítico).
    function renderConversionEvent(conv) {
        if (!conv) return null;   // propuesta vieja sin el campo → degrada en silencio
        const clientNames = conv.client_selected_names || [];
        if (['SELECTED', 'INHERITED'].includes(conv.mode) && (conv.names || []).length > 0) {
            return (
                <span className="mp-conv-event">
                    <span className="mp-conv-event-label">{t(`${NS}.conversion_event_label`)}</span>
                    <span className="mp-conv-event-value">
                        {t(`${NS}.${conv.mode === 'INHERITED'
                            ? 'conversion_event_inherited' : 'conversion_event_selected'}`, {
                            names: conv.names.join(', '), interpolation: {escapeValue: false},
                        })}
                    </span>
                </span>
            );
        }
        const noneSelected = conv.mode === 'NONE_SELECTED';
        return (
            <span className="mp-conv-event -warn">
                <span className="mp-conv-event-label">{t(`${NS}.conversion_event_label`)}</span>
                <span className="info-icon-container mp-conv-event-warnwrap">
                    <span className="mp-conv-event-value">
                        <FaIcon iconName="triangle-exclamation" className="mp-conv-event-icon"/>
                        {t(`${NS}.${noneSelected
                            ? 'conversion_event_none' : 'conversion_event_all_primary'}`)}
                    </span>
                    <span className="warning-tooltip -below -rich">
                        <span className="warning-tooltip-body">
                            <span className="warning-tooltip-text">
                                {t(`${NS}.${noneSelected
                                    ? 'conversion_event_none_tooltip' : 'conversion_event_tooltip'}`)}
                                {/* Los eventos preseleccionados del par cliente↔cuenta son CONTEXTO
                                    (la línea nueva NO los hereda) → viven en el tooltip. Con
                                    NONE_SELECTED son, además, los candidatos obvios a elegir. */}
                                {clientNames.length > 0 && (
                                    <>{' '}{t(`${NS}.conversion_event_client_hint`, {
                                        names: clientNames.join(', '), interpolation: {escapeValue: false},
                                    })}</>
                                )}
                            </span>
                        </span>
                    </span>
                </span>
            </span>
        );
    }

    // Campo del NOMBRE de la línea que se va a crear (card zMQIrxFe). Usa el mismo `form-label` +
    // `form-control` que el editor de la línea de control, para que se lea como el mismo campo que el
    // usuario ya conoce. `maxLength` es el del modelo: el límite se siente al escribir, no como un
    // error al aplicar. La edición vive en el estado local y viaja en el patch del approve — igual que
    // el presupuesto y los objetivos, y con la misma contrapartida: responder una duda regenera la
    // propuesta y la edición no guardada se pierde (los items nacen con PK nueva).
    function renderNameEditor(item) {
        const value = effectiveName(item);
        const blank = !value.trim();
        return (
            <div className="mp-name-edit">
                <label className="form-label" htmlFor={`mp-name-${item.id}`}>
                    {t(`${NS}.name_label`)}
                </label>
                {/* El error no puede comunicarse SÓLO por color: sin `aria-invalid` y sin asociar la
                    ayuda con `aria-describedby`, un lector de pantalla no se entera de por qué
                    Aprobar dejó de estar disponible. El texto pasa a `role="alert"` cuando avisa. */}
                <input id={`mp-name-${item.id}`} type="text"
                       className={`form-control mp-name-input ${blank ? '-invalid' : ''}`}
                       name={`mp-name-${item.id}`}
                       value={value}
                       maxLength={NAME_MAX_LENGTH}
                       aria-invalid={blank}
                       aria-describedby={`mp-name-hint-${item.id}`}
                       onChange={(e) => setEditedNames(
                           {...editedNames, [rowIdentity(item)]: e.target.value})}/>
                <span id={`mp-name-hint-${item.id}`}
                      className={`mp-name-hint ${blank ? '-invalid' : ''}`}
                      role={blank ? 'alert' : undefined}>
                    {blank ? t(`${NS}.name_required`) : t(`${NS}.name_hint`)}
                </span>
            </div>
        );
    }

    // Fila "Objetivo #2" vacía (paridad con el editor de la línea): cuando el plan trae UN solo
    // objetivo, se ofrece la segunda fila apagada — habilitarla permite sumar métrica + valor, que
    // viajan en el patch del approve. Solo cuenta si quedan las dos cosas completas.
    function renderAddedGoalRow(item) {
        const extra = addedGoals[item.id] || {enabled: false, metricName: null, value: ''};
        const setExtra = (changes) => setAddedGoals({
            ...addedGoals, [item.id]: {...extra, ...changes},
        });
        return (
            <div className="mp-goal-config row mb-3 align-items-start">
                <div className="col-md-auto">
                    <div className="switch-input d-flex align-items-center">
                        <Switch centered={true} checked={extra.enabled}
                                onChange={(e) => setExtra({enabled: e.target.checked})}
                                name={`mp-goal-extra-${item.id}`} style={{marginLeft: '0'}}/>
                        <div>{t('goal_configuration.goal_number', {number: 2})}</div>
                    </div>
                </div>
                <div className="col-md">
                    <div className="row g-3">
                        <div className="col-md-7">
                            <label>{t('goal_configuration.metric')}</label>
                            <MetricCombobox
                                name={`mp-goal-extra-metric-${item.id}`}
                                value={extra.enabled ? extra.metricName : null}
                                disabled={!extra.enabled}
                                customMetrics={customMetrics}
                                onChange={(name) => setExtra({metricName: name})}/>
                        </div>
                        <div className="col-md-5">
                            <label>{t('goal_configuration.value')}</label>
                            <DecimalInput name={`mp-goal-extra-value-${item.id}`}
                                          readOnly={!extra.enabled}
                                          onChange={(v) => setExtra({value: v})}
                                          value={extra.value}/>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
